import { signInWithPopup, getAdditionalUserInfo, getRedirectResult } from 'firebase/auth';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { DEFAULT_BG_REMOVAL_CONFIG, type BgRemovalConfig } from './bgRemoval/config';
import type { ImageVariant } from './bgRemoval/variants';
import { firebaseAuth, googleProvider } from './lib/firebase';
import { t, getLang } from './i18n';
import { storedAttribution } from './attribution';

// OBS: signInWithRedirect övervägdes för mobil (signInWithPopup är opålitlig
// i vissa mobila webbläsare), men fungerar INTE i det här projektet som det
// är konfigurerat idag: qvestium.com hostas under Firebase-projektet
// "qvestium" (se .firebaserc), medan Auth/identiteten körs mot ett annat
// projekt ("qvenari-prod", delat med Qid/Qvenari — se authDomain i
// lib/firebase.ts). Firebase Hostings reserverade /__/auth/handler-sida
// löser då inte tillbaka till rätt Auth-instans, så getRedirectResult()
// returnerar null även efter en lyckad Google-inloggning — användaren
// hamnar tillbaka utloggad. En riktig fix kräver en egen auth-domän kopplad
// till qvenari-prod (t.ex. via Firebase Hostings multi-site/authDomain-stöd),
// inte bara ett kodfix här.
export type PasswordLoginResult = { user: QidUser } | { verificationRequired: true; email: string; message: string };

async function handlePasswordLogin(res: Response, fallbackError: string): Promise<PasswordLoginResult> {
  const data = await res.json().catch(() => ({})) as any;
  if (!res.ok) throw new Error(data?.error ?? fallbackError);
  if (data.verification_required) return { verificationRequired: true, email: data.email, message: data.message };
  setTokens(data.access_token, data.id_token ?? data.access_token);
  auth.setUser(data.user);
  return { user: data.user as QidUser };
}

/** Google-kontot har inget Qvestium-konto än — be om inbjudningskod och försök igen med samma token. */
export class InviteRequiredError extends Error {
  constructor(message: string, public readonly idToken: string) { super(message); }
}

async function exchangeFirebaseIdToken(idToken: string, isNewUser: boolean, inviteCode?: string, acceptedTermsVersion?: string): Promise<{ user: QidUser; isNewUser: boolean }> {
  const res = await fetch(apiUrl('/auth/firebase-login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, inviteCode, acceptedTermsVersion, attribution: inviteCode ? storedAttribution() : undefined }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    if (err?.code === 'invite_required') throw new InviteRequiredError(err.error ?? t('api.enterInviteCode'), idToken);
    throw new Error(err?.error ?? t('api.googleLoginFailed'));
  }

  const data = await res.json() as { access_token: string; id_token?: string; user: QidUser };
  setTokens(data.access_token, data.id_token ?? data.access_token);
  auth.setUser(data.user);
  return { user: data.user, isNewUser };
}

// I produktion körs frontend (Firebase Hosting) och API:et (Cloud Run) på
// olika domäner — VITE_API_URL bakas in vid bygget (se deploy-firebase.sh)
// och alla relativa anrop prefixas med den. I dev-läge är VITE_API_URL tom
// och anropen går relativt genom Vites dev-proxy (vite.config.ts), precis
// som innan.
const API_BASE = ((import.meta as any).env?.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

const API_URL = apiUrl('/graphql');
const API_KEY = 'dev-secret';

// ─── AUTH HELPERS ──────────────────────────────────────────────

function getAccessToken(): string | null {
  return localStorage.getItem('qvestium_access_token');
}

function setTokens(access_token: string, id_token?: string, refresh_token?: string) {
  localStorage.setItem('qvestium_access_token', access_token);
  if (id_token) localStorage.setItem('qvestium_id_token', id_token);
  if (refresh_token) localStorage.setItem('qvestium_refresh_token', refresh_token);
}

function clearTokens() {
  localStorage.removeItem('qvestium_access_token');
  localStorage.removeItem('qvestium_id_token');
  localStorage.removeItem('qvestium_refresh_token');
  localStorage.removeItem('qvestium_user');
}

export interface QidUser {
  sub: string;
  email: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  locale?: string;
  picture?: string;
  verification_level?: number;
  auth_method?: string;
}

// Används av auth.loginPopup() och App.tsx:s callback-hantering för att
// relayera OIDC-koden från popup-fönstret tillbaka till huvudfliken. Vi
// använder localStorage + 'storage'-eventet istället för window.opener/
// postMessage eftersom Google och Qid sätter Cross-Origin-Opener-Policy-
// headrar som permanent kan klippa av window.opener mitt i redirect-kedjan
// (Qvestium → Qid → Google → Qid → Qvestium) — localStorage är origin-
// bundet och opåverkat av det.
// iOS-appen (Capacitor, se capacitor.config.ts) visar qvestium.com i en
// WKWebView. Där stoppar Google all inloggning, så Google går via Qid i
// systemets inloggningsfönster (QidAuthPlugin i ios/App/App/ViewController.swift,
// samma som Qvenari-appen) och svaret kommer tillbaka till se.qvestium.app://.
export const IS_NATIVE_APP = Capacitor.isNativePlatform();
const NATIVE_CALLBACK_SCHEME = 'se.qvestium.app';
const NATIVE_REDIRECT_URI = `${NATIVE_CALLBACK_SCHEME}://auth/callback`;
const QidAuth = registerPlugin<{
  start(options: { url: string; callbackScheme: string }): Promise<{ url: string }>;
}>('QidAuth');

export const POPUP_WINDOW_NAME = 'qid-login-popup';
export const POPUP_RESULT_KEY = 'qvestium_qid_popup_result';

/** Fel från Qid när inloggningen via en leverantör inte gav någon kod. */
function qidLoginError(error: string | null | undefined, provider?: string): Error {
  if (error === 'access_denied') return Object.assign(new Error(t('api.popupClosed')), { code: 'auth/popup-closed-by-user' });
  if (error === 'account_not_found') {
    return new Error(provider === 'apple' ? t('api.appleNoAccount') : IS_NATIVE_APP ? t('api.googleNoAccountInApp') : t('api.noAccount'));
  }
  return new Error(provider === 'apple' ? t('api.appleLoginFailed') : (error ?? t('api.googleLoginFailed')));
}

export const auth = {
  getAccessToken,
  setTokens,
  clearTokens,

  isLoggedIn: () => !!getAccessToken(),

  getUser: (): QidUser | null => {
    const raw = localStorage.getItem('qvestium_user');
    return raw ? JSON.parse(raw) : null;
  },

  setUser: (user: QidUser) => {
    localStorage.setItem('qvestium_user', JSON.stringify(user));
  },

  // Get OIDC config from backend
  getConfig: async () => {
    const res = await fetch(apiUrl('/auth/config'));
    return res.json() as Promise<{
      issuer: string;
      client_id: string;
      authorization_endpoint: string;
      redirect_uri: string;
      scope: string;
    }>;
  },

  // Start OIDC login — redirect to Qid
  login: async () => {
    const config = await auth.getConfig();
    const state = crypto.randomUUID();
    sessionStorage.setItem('oidc_state', state);

    const url = new URL(config.authorization_endpoint);
    url.searchParams.set('client_id', config.client_id);
    url.searchParams.set('redirect_uri', config.redirect_uri);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', config.scope);
    url.searchParams.set('state', state);

    window.location.href = url.toString();
  },

  // Start OIDC login i ett popup-fönster istället för att navigera bort
  // hela qvestium.com — popupen sköter hela Qid/Google-omvägen och lämnar
  // resultatet i localStorage (se POPUP_RESULT_KEY ovan och App.tsx) när
  // den landar tillbaka på qvestium.com igen, varefter den stänger sig
  // själv. Faller tillbaka till helsidesomdirigering om popupen blockeras.
  // extra: t.ex. { provider: 'apple', allow_create: '0' } — Qid går då direkt
  // till Apple och skapar inga nya konton (inbjudningskoden kan inte kringgås).
  loginPopup: (extra: Record<string, string> = {}): Promise<QidUser> => {
    return new Promise((resolve, reject) => {
      (async () => {
        const config = await auth.getConfig();
        const state = crypto.randomUUID();
        sessionStorage.setItem('oidc_state', state);

        const url = new URL(config.authorization_endpoint);
        url.searchParams.set('client_id', config.client_id);
        url.searchParams.set('redirect_uri', config.redirect_uri);
        url.searchParams.set('response_type', 'code');
        url.searchParams.set('scope', config.scope);
        url.searchParams.set('state', state);
        for (const [k, v] of Object.entries(extra)) url.searchParams.set(k, v);

        const popup = window.open(url.toString(), POPUP_WINDOW_NAME, 'width=480,height=680');
        if (!popup) {
          reject(new Error(t('api.popupBlocked')));
          return;
        }

        let settled = false;
        const cleanup = () => {
          window.removeEventListener('storage', onStorage);
          clearInterval(closedCheck);
        };

        function onStorage(e: StorageEvent) {
          if (e.key !== POPUP_RESULT_KEY || !e.newValue) return;
          settled = true;
          cleanup();
          localStorage.removeItem(POPUP_RESULT_KEY);
          const { code, state: returnedState, error } = JSON.parse(e.newValue);
          if (!code) {
            sessionStorage.removeItem('oidc_state');
            reject(qidLoginError(error, extra.provider));
            return;
          }
          auth.handleCallback(code, returnedState).then(resolve, reject);
        }
        window.addEventListener('storage', onStorage);

        const closedCheck = setInterval(() => {
          if (popup.closed && !settled) {
            cleanup();
            reject(new Error(t('api.popupClosed')));
          }
        }, 500);
      })().catch(reject);
    });
  },

  // Exchange authorization code for tokens
  handleCallback: async (code: string, state: string): Promise<QidUser> => {
    const savedState = sessionStorage.getItem('oidc_state');
    if (savedState && savedState !== state) {
      throw new Error(t('api.stateMismatch'));
    }
    sessionStorage.removeItem('oidc_state');

    const config = await auth.getConfig();
    return auth.exchangeCode(code, config.redirect_uri);
  },

  // Byt Qids engångskod mot tokens och hämta användaren.
  exchangeCode: async (code: string, redirectUri: string): Promise<QidUser> => {
    const res = await fetch(apiUrl('/auth/token'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as any).error ?? t('api.tokenExchangeFailed'));
    }

    const tokens = await res.json() as { access_token: string; id_token?: string; refresh_token?: string };
    setTokens(tokens.access_token, tokens.id_token, tokens.refresh_token);

    const headers: Record<string, string> = { Authorization: `Bearer ${tokens.access_token}` };
    if (tokens.id_token) headers['X-Id-Token'] = tokens.id_token;
    const userRes = await fetch(apiUrl('/auth/me'), { headers });

    if (!userRes.ok) throw new Error(t('api.fetchUserFailed'));
    const user = await userRes.json() as QidUser;
    auth.setUser(user);
    return user;
  },

  // Direct login via Qid (email + password). Overifierad e-post (t.ex.
  // första inloggningen) ger ingen session utan en mejlad kod — se verifyEmailCode.
  directLogin: async (email: string, password: string): Promise<PasswordLoginResult> => {
    const res = await fetch(apiUrl('/auth/direct-login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return handlePasswordLogin(res, t('api.loginFailed'));
  },

  // Skapa nytt konto (e-post + lösenord + inbjudningskod) — precis som
  // directLogin blir det ett riktigt Qid-konto bakom kulisserna (delad
  // identitet med övriga Q-appar), men känns som ett vanligt konto på
  // qvestium.com. Kontot aktiveras med koden som mejlas.
  register: async (email: string, password: string, inviteCode: string, acceptedTermsVersion?: string, displayName?: string): Promise<PasswordLoginResult> => {
    const res = await fetch(apiUrl('/auth/register'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, inviteCode, acceptedTermsVersion, display_name: displayName, attribution: storedAttribution() }),
    });
    return handlePasswordLogin(res, t('api.registrationFailed'));
  },

  verifyEmailCode: async (email: string, code: string): Promise<QidUser> => {
    const res = await fetch(apiUrl('/auth/verify-email-code'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    const result = await handlePasswordLogin(res, t('api.wrongCode'));
    if ('verificationRequired' in result) throw new Error(t('api.wrongCode'));
    return result.user;
  },

  // Glömt lösenord: en kod mejlas (svaret är detsamma om kontot inte finns),
  // sedan byts lösenordet med koden och man loggas in. Se api/src/routes/auth.ts.
  forgotPassword: async (email: string): Promise<string> => {
    const res = await fetch(apiUrl('/auth/forgot-password'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({})) as any;
    if (!res.ok) throw new Error(data?.error ?? t('api.resendCodeFailed'));
    return data.message ?? t('api.newCodeSent');
  },

  resetPassword: async (email: string, code: string, password: string): Promise<QidUser> => {
    const res = await fetch(apiUrl('/auth/reset-password'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, password }),
    });
    const result = await handlePasswordLogin(res, t('api.wrongCode'));
    if ('verificationRequired' in result) throw new Error(t('api.wrongCode'));
    return result.user;
  },

  resendEmailCode: async (email: string): Promise<string> => {
    const res = await fetch(apiUrl('/auth/resend-email-code'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({})) as any;
    if (!res.ok) throw new Error(data?.error ?? t('api.resendCodeFailed'));
    return data.message ?? t('api.newCodeSent');
  },

  // Logga in med Google — Firebase-popupen visar bara Googles kontoväljare
  // (aldrig Qid), sedan verifieras token server-side och blir en Qid-session.
  // isNewUser (från Firebase, inte Qid) används för att avgöra om detta var
  // en förstagångsregistrering — se handleLogin i App.tsx som då skickar
  // användaren till Min garderob.
  // Firebase laddar en dold iframe första gången popupen öppnas. Hinner den
  // inte bli klar direkt efter klicket räknar Safari/Firefox inte längre
  // fönstret som öppnat av användaren och blockerar det (auth/popup-blocked).
  // getRedirectResult() laddar samma iframe i förväg och gör inget annat när
  // ingen omdirigering pågår — anropas när inloggningsrutan öppnas.
  prepareGoogleLogin: () => { if (!IS_NATIVE_APP) getRedirectResult(firebaseAuth).catch(() => {}); },

  googleLoginWithInvite: (idToken: string, inviteCode: string, acceptedTermsVersion?: string) => exchangeFirebaseIdToken(idToken, true, inviteCode, acceptedTermsVersion),

  googleLogin: async (): Promise<{ user: QidUser; isNewUser: boolean }> => {
    if (IS_NATIVE_APP) return { user: await auth.googleLoginViaQid(), isNewUser: false };
    const result = await signInWithPopup(firebaseAuth, googleProvider);
    const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
    const idToken = await result.user.getIdToken();
    return exchangeFirebaseIdToken(idToken, isNewUser);
  },

  // Logga in med Apple — via Qid (provider=apple), som visar Apples ruta med
  // Qvestiums namn (eget Services ID). Bara befintliga konton: allow_create=0,
  // så att inbjudningskoden inte kan kringgås; nya användare skapar kontot med
  // e-post + kod (eller Google på webben) och kan sedan logga in med Apple.
  appleLogin: (): Promise<QidUser> =>
    IS_NATIVE_APP ? auth.qidProviderLoginNative('apple') : auth.loginPopup({ provider: 'apple', allow_create: '0' }),

  googleLoginViaQid: (): Promise<QidUser> => auth.qidProviderLoginNative('google'),

  // Google/Apple i iOS-appen: Qid (provider=…) i systemets inloggningsfönster.
  // allow_create=0 — Qid skapar inga nya konton här, så inbjudningskoden kan
  // inte kringgås; nya användare skapar kontot med e-post + kod i appen.
  qidProviderLoginNative: async (provider: 'google' | 'apple'): Promise<QidUser> => {
    const config = await auth.getConfig();
    const state = crypto.randomUUID();
    const url = new URL(config.authorization_endpoint);
    url.searchParams.set('client_id', config.client_id);
    url.searchParams.set('redirect_uri', NATIVE_REDIRECT_URI);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', config.scope);
    url.searchParams.set('state', state);
    url.searchParams.set('provider', provider);
    url.searchParams.set('allow_create', '0');

    let callback: string;
    try {
      callback = (await QidAuth.start({ url: url.toString(), callbackScheme: NATIVE_CALLBACK_SCHEME })).url;
    } catch (err: any) {
      // Samma felkod som Firebase-popupen, så LoginModal visar "avbruten".
      if (err?.code === 'CANCELED') throw Object.assign(new Error(t('api.popupClosed')), { code: 'auth/popup-closed-by-user' });
      throw err;
    }
    const params = new URL(callback).searchParams;
    if (params.get('state') !== state) throw new Error(t('api.stateMismatch'));
    const code = params.get('code');
    if (!code) throw qidLoginError(params.get('error'), provider);
    return auth.exchangeCode(code, NATIVE_REDIRECT_URI);
  },

  // Logout
  logout: () => {
    clearTokens();
    window.location.href = '/';
  },

  // Fetch current user (validate token)
  fetchMe: async (): Promise<QidUser | null> => {
    const token = getAccessToken();
    if (!token) return null;
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      const idToken = localStorage.getItem('qvestium_id_token');
      if (idToken) headers['X-Id-Token'] = idToken;
      const res = await fetch(apiUrl('/auth/me'), { headers });
      if (!res.ok) { clearTokens(); return null; }
      const user = await res.json() as QidUser;
      auth.setUser(user);
      return user;
    } catch {
      clearTokens();
      return null;
    }
  },
};

// Bilder som laddats upp via /api/upload sparas i databasen som relativa
// sökvägar ("/uploads/xyz.png") — fungerar lokalt via Vites dev-proxy, men i
// produktion körs frontend och API på olika domäner (Firebase Hosting resp.
// Cloud Run), så en relativ sökväg pekar mot FEL server (t.ex. qvestium.com/
// uploads/... som inte finns) och bilden visas aldrig. Går rekursivt igenom
// alla svar (GraphQL-data och REST-JSON) och gör om sådana sökvägar till
// absoluta URL:er mot API_BASE — täcker alla bildfält (imageUrl,
// galleryUrls, avatarUrl, bodyPhotos, msg.imageUrl, osv) på ett ställe,
// istället för att behöva ändra varje enskild <img src=...>.
function absolutizeUploadUrls<T>(value: T): T {
  if (typeof value === 'string') {
    return (value.startsWith('/uploads/') ? apiUrl(value) : value) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map(absolutizeUploadUrls) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = absolutizeUploadUrls(v);
    return out as T;
  }
  return value;
}

// ── Outfit-förslag (api/src/outfits/types.ts) ──
export type OutfitAcquire = 'own' | 'buy' | 'rent';
export interface OutfitItem {
  id: string; name: string; imageUrl: string | null; slot: string;
  acquire: OutfitAcquire; price: number | null; rentPerMonth: number | null; link: string;
}
export interface Outfit {
  title: string; why: string; items: OutfitItem[];
  totals: { buy: number; rentPerMonth: number }; collageUrl: string | null;
}
export interface OutfitSuggestion {
  id: string; outfits: Outfit[]; hasBodyPhoto: boolean;
  brief: { summary: string; styles: string[]; occasion: string };
}

// Inloggningsheaders för REST-anrop (samma som gql() skickar). X-Id-Token
// behövs: Qids åtkomsttoken från OIDC-inloggning (Google/Apple via Qid)
// saknar e-post, så API:t läser den ur id_token — utan den blir det 401.
export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const idToken = localStorage.getItem('qvestium_id_token');
  if (idToken) headers['X-Id-Token'] = idToken;
  return headers;
}

// REST-anrop för admin (JSON in/ut, fel som Error med serverns text).
// ── CRM för rekrytering (api/src/crm/leads.ts, Admin → CRM) ──
export type CrmStage = 'identified' | 'contacted' | 'replied' | 'registered' | 'active' | 'lost';
export const CRM_STAGES: CrmStage[] = ['identified', 'contacted', 'replied', 'registered', 'active', 'lost'];
export const CRM_PLATFORMS = ['vinted', 'tradera', 'sellpy', 'plick', 'blocket', 'instagram', 'facebook', 'tiktok', 'other'] as const;
export interface CrmLead {
  id: string; platform: string; profileUrl: string | null; handle: string | null; displayName: string | null;
  contactEmail: string | null; contactPhone: string | null; location: string | null; categories: string | null;
  audience: string | null; description: string | null; stage: CrmStage; priority: 'A' | 'B' | 'C' | null;
  owner: string | null; nextStep: string | null; nextStepDate: string | null; inviteCode: string;
  registeredEmail: string | null; registeredAt: string | null; activeAt: string | null; noteCount: number;
  createdAt: string; updatedAt: string;
}
export interface CrmNote { id: string; kind: 'note' | 'dm' | 'email' | 'call'; author: string; body: string; createdAt: string }
export type CrmLeadDetail = CrmLead & { notes: CrmNote[] };
export interface CrmImportRow { url: string; platform: string | null; handle: string | null; action: 'insert' | 'skip'; reason: string | null }

// ── Marknadsföring (api/src/marketing, Admin → Marknadsföring) ──
export type MarketingPlatform = 'meta' | 'tiktok';
export type CampaignStatus = 'draft' | 'live' | 'paused' | 'done';
export type CreativeStatus = 'idea' | 'production' | 'ready' | 'live' | 'paused' | 'stopped';
export interface ChecklistItem { id: string; when: string; text: string; owner?: string | null; done: boolean }
export interface MarketingCreative {
  id: string; campaignId: string; concept: string; variantKey: string; hook: string | null; adText: string | null; cta: string | null;
  landingPath: string; desiredAction: string | null; script: string | null; mediaUrl: string | null; mediaType: 'video' | 'image' | null;
  status: CreativeStatus; metaAdId: string | null; tiktokAdId: string | null; sortOrder: number; links: Record<MarketingPlatform, string>;
}
export interface MarketingCampaign {
  id: string; name: string; utmCampaign: string; inviteCode: string; status: CampaignStatus; budgetKr: number | null; dailyBudgetKr: number | null;
  startDate: string | null; endDate: string | null; audience: string | null; notes: string | null; checklist: ChecklistItem[];
  meta: { campaignId: string | null; adsetId: string | null }; tiktok: { campaignId: string | null; adgroupId: string | null };
}
export type MarketingCampaignDetail = MarketingCampaign & { creatives: MarketingCreative[] };
export interface MarketingMetrics {
  spend: number; impressions: number; clicks: number; videoViews2s: number; videoViews6s: number; platformLandingViews: number; visits: number;
  registrations: number; activated7d: number; activationPending: number; requests: number; transactions: number;
  ctr: number | null; costPerVisit: number | null; costPerRegistration: number | null; costPerActivated: number | null; activationRate: number | null;
}
export interface MarketingResults {
  campaignId: string; total: MarketingMetrics; unverifiedRegistrations: number; budgetLeft: number | null;
  variants: (MarketingMetrics & { variantKey: string; concept: string; hook: string | null; status: CreativeStatus })[];
  unattributed: MarketingMetrics; platforms: (MarketingMetrics & { platform: string })[];
  daily: { day: string; spend: number; visits: number; registrations: number }[]; lastSync: Partial<Record<MarketingPlatform, string | null>>;
}
export interface MarketingPlatformStatus { platform: MarketingPlatform; configured: boolean; missing: string[]; ok?: boolean; account?: string | null; currency?: string | null; error?: string | null }

async function adminRest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: { ...authHeaders(), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string })?.error ?? `HTTP ${res.status}`);
  return data as T;
}

async function gql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-Api-Key': API_KEY };
  const token = getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // Qid-utfärdade OIDC access_token saknar e-postclaim (se resolvers.ts på
  // API:et) — id_token behövs för att backend ska kunna slå upp inloggad
  // användares garderob m.m. när man loggat in via Qid SSO/Google.
  const idToken = localStorage.getItem('qvestium_id_token');
  if (idToken) headers['X-Id-Token'] = idToken;

  const res = await fetch(API_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json() as { data?: T; errors?: { message: string }[] };
  if (json.errors?.length) throw new Error(json.errors[0].message);
  notifyListingsChanged(query);
  return absolutizeUploadUrls(json.data as T);
}

// Ändringar som påverkar marknadsplatsen (annonser, drops, byten, bud, gilla)
// meddelas App, som hämtar om annonslistan — annars syns en redigerad annons
// på Plagg först efter omladdning. Se 'qvestium:listings-changed' i App.tsx.
const LISTING_MUTATIONS = /^(create|update|delete)WardrobeItem$|^(createDrop|cancelDrop)$|Swap|Offer$|^(un)?likeWardrobeItem$|^(confirm|ship|request)/;
function notifyListingsChanged(query: string) {
  if (!/^\s*mutation\b/.test(query)) return;
  const field = query.slice(query.indexOf('{') + 1).match(/^\s*(\w+)/)?.[1] ?? '';
  if (LISTING_MUTATIONS.test(field)) window.dispatchEvent(new CustomEvent('qvestium:listings-changed', { detail: field }));
}

// ─────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────

export type FashionCategory = 'Kvinna' | 'Man' | 'Barn' | 'Unisex';
export type FashionStyle = 'Klassisk' | 'Modern' | 'Streetwear' | 'Formell' | 'Casual' | 'Bohemisk';

export interface FashionCollection {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  location: string;
  region: string;
  brand?: string;
  designer?: string;
  category: FashionCategory;
  style: FashionStyle;
  season?: string;
  price: number;
  monthlyPrice: number;
  priceUnit: string;
  spotsTotal: number;
  spotsAvailable: number;
  imageUrl?: string;
  galleryUrls: string[];
  videoUrl?: string;
  highlights: string[];
  includes: string[];
  featured: boolean;
  status: string;
  contactName?: string;
  contactEmail?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FashionBooking {
  id: string;
  collectionId: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  size?: string;
  notes?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface FashionProfile {
  id: string;
  userId: string;
  displayName?: string;
  address?: string;
  addressStreet?: string;
  addressPostalCode?: string;
  addressCity?: string;
  // Frakt via Fraktjakt: namn på paket och mobilnummer för avisering
  shippingName?: string;
  // Privatperson eller företag (api/src/account/sellerType.ts)
  sellerType?: 'private' | 'business' | null;
  sellerTypeConfirmed?: boolean;
  businessName?: string | null;
  businessOrgNumber?: string | null;
  businessVatNumber?: string | null;
  businessAddress?: string | null;
  phone?: string;
  addressCountry?: string;
  bio?: string;
  avatarUrl?: string;
  size?: string;
  bustCm?: number;
  waistCm?: number;
  hipCm?: number;
  stylePreferences: string[];
  favoriteBrands: string[];
  bodyPhotos: string[];
  notes?: string;
  is_admin: boolean;
  // Ungefärligt område (~1 km) för adressen — det andra användare ser.
  area?: GeoArea | null;
  createdAt: string;
  updatedAt: string;
}

export interface GeoArea { lat: number; lng: number; radiusM: number }
export interface ApiToken { id: string; name: string; prefix: string; createdAt: string; lastUsedAt?: string | null }
export interface SellerArea { userId: string; area: GeoArea }

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  monthlyPrice: number;
  price3?: number | null;
  price24?: number | null;
  price1?: number | null;
  priceUnit: string;
  category: string;
  imageUrl?: string;
  galleryUrls?: string[];
  size?: string;
  condition?: number;
  inStock: boolean;
  sortOrder: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface FortnoxAccountPlan {
  cash: number; sellerLiability: number; insuranceLiability: number;
  protectionFeeRevenue: number; commissionRevenue: number; featureRevenue: number; shippingRevenue: number; outputVat: number;
}
export interface FortnoxStatus {
  configured: boolean; connected: boolean; companyName: string | null; voucherSeries: string;
  accounts: FortnoxAccountPlan; defaultAccounts: FortnoxAccountPlan; unbookedEvents: number;
  autoBookEnabled: boolean; autoBookLastDay: string | null;
}
export interface FortnoxVoucherRow { account: number; debit: number; credit: number; description: string }
export type FortnoxCategory = 'in' | 'out' | 'refund';
export interface FortnoxDraft { day: string; category: FortnoxCategory; description: string; eventIds: string[]; rows: FortnoxVoucherRow[] }
export interface FortnoxVoucher {
  id: string; day: string; category: FortnoxCategory; description: string; rows: FortnoxVoucherRow[];
  status: 'booking' | 'booked' | 'error'; fortnoxSeries: string | null; fortnoxNumber: number | null; error: string | null; bookedBy: string | null; updatedAt: string;
}
export interface FortnoxPreview { drafts: FortnoxDraft[]; vouchers: FortnoxVoucher[]; backfilledCount: number }

export interface WardrobeItem {
  // Säljaren som privatperson eller företag — avgör köparens konsumentskydd
  seller?: { type: 'private' | 'business' | null; businessName?: string | null; orgNumber?: string | null; vatNumber?: string | null; address?: string | null };
  // Säljarens visningsnamn; userId är ett publikt id ("u_…") för andra än en själv
  sellerName?: string;
  id: string;
  userId: string;
  name: string;
  description?: string;
  category: string;
  size?: string;
  condition?: number;
  imageUrl?: string;
  galleryUrls?: string[];
  /** Frilagda bilder och deras original (bgRemoval/variants.ts). Tom för äldre annonser. */
  imageVariants?: ImageVariant[];
  forRent: boolean;
  rentPrice: number;
  rentPrice3?: number | null;
  rentPrice24?: number | null;
  rentPrice1?: number | null;
  forSale: boolean;
  salePrice: number;
  forSwap: boolean;
  source: string;
  sourceOrderId?: string;
  qregistryObjectId?: string;
  status: string;
  dropId?: string | null;
  dropName?: string | null;
  dropReleaseAt?: string | null;
  dropStatus?: string | null;
  likeCount: number;
  likedByMe: boolean;
  // Viktklass för fraktprisberäkning — '0-1' | '1-2' | '2-3' (kg).
  weightTier: string;
  // Utvald annons överst i marknaden. featuredUntil saknas när admin valt ut den tills vidare.
  isFeatured?: boolean;
  featuredAt?: string | null;
  featuredUntil?: string | null;
  // Satt när admin tagit bort annonsen (status 'removed').
  moderationReason?: string | null;
  // Ersättningsvärde vid skada/utebliven retur (se api/src/returns/returns.ts)
  replacementValue?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface WardrobeItemLike {
  userId: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  createdAt: string;
}

export interface Drop {
  id: string;
  userId: string;
  name: string;
  releaseAt: string;
  status: string;
  items: WardrobeItem[];
  createdAt: string;
  updatedAt: string;
}

// Subset returned by the public (unauthenticated) share-link query — no seller-identifying fields.
export interface PublicDrop {
  id: string;
  name: string;
  releaseAt: string;
  status: string;
  createdAt: string;
  items: Pick<WardrobeItem, 'id' | 'name' | 'description' | 'category' | 'size' | 'condition' | 'imageUrl' | 'galleryUrls' | 'forRent' | 'rentPrice' | 'rentPrice3' | 'rentPrice24' | 'rentPrice1' | 'forSale' | 'salePrice' | 'forSwap' | 'status'>[];
}

// ── Frakt via Fraktjakt (api/src/shipping/) ──
export interface ShippingOption {
  id: string; productName: string; carrier?: string | null; logoUrl?: string | null; description: string;
  arrivalTime: string; toAgent: boolean; agentInfo?: string | null; price: number;
}
export interface Shipment {
  id: string; provider: string; status: string; carrier?: string | null; trackingNumber?: string | null;
  trackingLink?: string | null; labelUrls: string[]; agentInfo?: string | null; bookingFailed: boolean;
}
const SHIPMENT_FIELDS = 'id provider status carrier trackingNumber trackingLink labelUrls agentInfo bookingFailed';
const SHIPPING_OPTION_FIELDS = 'id productName carrier logoUrl description arrivalTime toAgent agentInfo price';

export interface SwapProposal {
  myShipment?: Shipment | null;
  incomingShipment?: Shipment | null;
  proposerName?: string;
  ownerName?: string;
  id: string;
  proposerUserId: string;
  ownerUserId: string;
  targetItem: WardrobeItem;
  offeredItems: WardrobeItem[];
  message?: string;
  ownerMessage?: string;
  durationMonths?: number | null;
  proposerSeenAt?: string | null;
  status: string;
  // Hämta (pickup) vs frakt (shipping) — se resolvers.ts tryFinalizeSwap.
  proposerDeliveryMethod: string;
  ownerDeliveryMethod?: string | null;
  proposerShippingCost: number;
  // Köparskydd som föreslagaren betalar med frakten (0 vid fysisk överlämning)
  proposerProtectionFee: number;
  ownerShippingCost: number;
  proposerShippingPaidAt?: string | null;
  ownerShippingPaidAt?: string | null;
  proposerHandoverConfirmedAt?: string | null;
  ownerHandoverConfirmedAt?: string | null;
  proposerLegReceivedAt?: string | null;
  ownerLegReceivedAt?: string | null;
  problemReportedAt?: string | null;
  meetingAddress?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOffer {
  buyerName?: string;
  sellerName?: string;
  id: string;
  item: WardrobeItem;
  buyerUserId: string;
  sellerUserId: string;
  type: string;
  offeredPrice: number;
  bindingMonths?: number;
  message?: string;
  sellerMessage?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// Subset returned by the public (unauthenticated) sellerProfile query — no
// body-measurement/notes fields are ever selected for arbitrary viewers.
export interface PublicSellerProfile {
  displayName?: string | null;
  id: string;
  userId: string;
  avatarUrl?: string;
  bio?: string;
  area?: GeoArea | null;
}

export interface ReturnBag {
  id: string;
  userId: string;
  bagCode: string;
  status: string;
  trackingNumber?: string;
  carrier: string;
  wardrobeItemId?: string;
  orderedAt: string;
  activatedAt?: string;
  shippedAt?: string;
  receivedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RentalCharge { id: string; monthIndex: number; monthsTotal: number; amount: number; dueAt: string; status: 'scheduled' | 'paid' | 'action_required' | 'canceled'; paidAt?: string | null; payoutStatus?: string | null }

export interface TaxInfoStatus {
  available: boolean; required: boolean; held: boolean; deadline: string | null;
  info: { kind: 'individual' | 'entity'; firstName: string | null; lastName: string | null; legalName: string | null; addressLine: string; postalCode: string; city: string; tinMasked: string; vatNumber: string | null; submittedAt: string } | null;
}

export interface WardrobeTransaction {
  // Köparen kan själv avbryta (betald, inte skickad) — api/src/payments/buyerCancel.ts
  buyerCanCancel?: boolean;
  // Hyra: månad 2–N (api/src/rentals/billing.ts); månad 1 betalas i kassan
  rentalCharges?: RentalCharge[];
  sellerName?: string;
  buyerName?: string;
  id: string;
  // Reklamationsfönster (api/src/payments/claims.ts)
  payoutDueAt?: string | null;
  problemReportedAt?: string | null;
  wardrobeItemId: string;
  sellerUserId: string;
  buyerUserId: string;
  type: string;
  bindingMonths?: number;
  itemPrice: number;
  commissionRate: number;
  commissionAmount: number;
  sellerPayout: number;
  status: string;
  returnBagId?: string;
  trackingNumber?: string;
  sellerTrackingNumber?: string;
  buyerReturnTracking?: string;
  shippedAt?: string;
  deliveredAt?: string;
  returnRequestedAt?: string;
  returnShippedAt?: string;
  returnReceivedAt?: string;
  itemName?: string;
  itemImageUrl?: string;
  // Hämta (pickup) vs frakt (shipping) — se confirmHandover i resolvers.ts.
  deliveryMethod: string;
  shippingCost: number;
  // fraktjakt = Qvestium bokar frakten och säljaren får en etikett
  shippingProvider?: string | null;
  shipment?: Shipment | null;
  sellerHandoverConfirmedAt?: string | null;
  buyerHandoverConfirmedAt?: string | null;
  // Hämtning: köparen vill avbryta (api/src/payments/buyerCancel.ts)
  buyerCancelStatus?: 'requested' | 'approved' | 'auto_approved' | 'objected' | 'withdrawn' | 'rejected' | null;
  buyerCancelRequestedAt?: string | null;
  buyerCancelReason?: string | null;
  // not_applicable | pending | transferred | failed
  payoutStatus?: string | null;
  protectionFee: number;
  createdAt: string;
  updatedAt: string;
}

// ── Admin: drift och moderering (se api/src/admin/operations.ts) ──
export interface AdminIssue {
  kind: 'stuck_payment' | 'not_shipped' | 'not_handed_over' | 'cancel_dispute' | 'not_delivered' | 'payout_failed' | 'payout_waiting';
  message: string;
  since: string;
  transaction: WardrobeTransaction;
}
export interface BlockedUser { userId: string; reason: string; blockedBy: string; createdAt: string }
export interface AdminAction { id: string; adminUserId: string; action: string; targetType: string; targetId: string; reason?: string | null; createdAt: string }
export interface AdminActionResult { ok: boolean; message: string }

// BankID-verifiering (Qid/Idura). test = verifierad mot Iduras testmiljö —
// räknas inte som verifierad.
export interface IdentityStatus { verified: boolean; test: boolean; method: string | null; verified_at: string | null; name: string | null }

// ── Ärenden: anmälningar (DSA) och support (se api/src/support/tickets.ts) ──
export interface SupportTicketMessage { id: string; author: 'user' | 'admin'; authorEmail?: string | null; body: string; internal: boolean; createdAt: string }
export interface SupportTicket {
  id: string; number: string; kind: 'report' | 'support' | 'dispute'; category: string; status: 'open' | 'in_progress' | 'closed';
  reporterEmail: string; reporterName?: string | null; reporterUserId?: string | null;
  targetType?: string | null; targetId?: string | null; targetName?: string | null; targetStatus?: string | null;
  message: string; resolution?: string | null; createdAt: string; updatedAt: string; closedAt?: string | null;
  messages: SupportTicketMessage[];
  agentTask?: AgentTask | null;
}
// Supportagentens svarsutkast (api/src/agent/tasks.ts) — godkänns av AGENT_APPROVERS.
export interface AgentTask {
  id: string; status: 'queued' | 'running' | 'proposed' | 'decided' | 'failed';
  proposal?: { reply: string; language: string; suggestedAction: 'none' | 'refund' | 'contact_seller' | 'escalate' | 'close'; reasoning: string } | null;
  model?: string | null; error?: string | null; decision?: 'approved' | 'edited' | 'rejected' | null; decidedBy?: string | null;
  createdAt: string; canDecide: boolean;
}
const AGENT_TASK_FIELDS = 'agentTask { id status proposal { reply language suggestedAction reasoning } model error decision decidedBy createdAt canDecide }';
const TICKET_FIELDS = `id number kind category status reporterEmail reporterName reporterUserId targetType targetId targetName targetStatus message resolution createdAt updatedAt closedAt messages { id author authorEmail body internal createdAt } ${AGENT_TASK_FIELDS}`;

export interface SupportTicketInput {
  kind: 'report' | 'support'; category: string; message: string; email?: string; name?: string;
  targetType?: 'wardrobe_item' | 'user' | 'message'; targetId?: string; website?: string;
}

// ── Återlämning av hyrda/lånade plagg (se api/src/returns/returns.ts) ──
export type ItemReturnStatus = 'active' | 'awaiting_review' | 'approved' | 'compensation_proposed' | 'compensation_agreed' | 'escalated' | 'handed_over' | 'shipping_booked' | 'completed';
// ── Betyg efter genomförd affär (se reviews/reviews.ts i API:t) ──
export type DealKind = 'buy' | 'rent' | 'swap';

export interface UserReview {
  id: string;
  rating: number;
  comment?: string | null;
  kind: DealKind;
  reviewerName: string;
  createdAt: string;
}

export interface UserReputation {
  userId: string;
  averageRating: number | null;
  reviewCount: number;
  buyCount: number;
  rentCount: number;
  swapCount: number;
  memberSince: string | null;
  reviews: UserReview[];
}

export interface PendingReview {
  sourceType: 'transaction' | 'swap';
  sourceId: string;
  kind: DealKind;
  counterpartyUserId: string;
  counterpartyName: string;
  itemName: string;
  itemImageUrl?: string | null;
  completedAt: string;
}

export interface FeaturePack { items: number; days: number; pricePerItemWeek: number; price: number }

export interface ItemReturn {
  id: string; role: 'borrower' | 'lender'; sourceType: 'rental' | 'swap'; status: ItemReturnStatus;
  itemName: string; itemImageUrl?: string | null; originalImages: string[]; photos: string[];
  dueAt: string; returnMethod?: 'dropoff' | 'shipping' | null; verdict?: string | null; lenderNote?: string | null;
  itemValue?: number | null; netRent: number; compensationAmount?: number | null;
  borrowerDecision?: 'accepted' | 'rejected' | null; lenderDecision?: 'accepted' | 'rejected' | null; compensationPaid: boolean;
  shippingCost: number; shippingAvailable: boolean; qrCodeLink?: string | null; labelFreeCode?: string | null; consignmentNumber?: string | null;
  dropoffAddress?: string | null; counterpartName: string; updatedAt: string;
  insuranceStatus?: string | null; insuranceClaimsFormUrl?: string | null;
  shipment?: Shipment | null;
}
const RETURN_FIELDS = 'shipment { id provider status carrier trackingNumber trackingLink labelUrls agentInfo bookingFailed } id role sourceType status itemName itemImageUrl originalImages photos dueAt returnMethod verdict lenderNote itemValue netRent compensationAmount borrowerDecision lenderDecision compensationPaid shippingCost shippingAvailable qrCodeLink labelFreeCode consignmentNumber dropoffAddress counterpartName insuranceStatus insuranceClaimsFormUrl updatedAt';

// ── Direktmeddelanden (se api/src/messages/messages.ts) ──
export interface ChatMessage { id: string; conversationId: string; mine: boolean; body: string; itemId?: string | null; item?: { id: string; name: string; imageUrl?: string | null } | null; flaggedReason?: string | null; createdAt: string }
export interface Conversation {
  id: string;
  otherUser: { id: string; displayName: string; avatarUrl?: string | null };
  lastMessage?: ChatMessage | null;
  unreadCount: number;
  item?: WardrobeItem | null;
  blockedByMe: boolean;
  updatedAt: string;
}

// Admin-only kommersiell överblick — se requireAdmin i resolvers.ts.
export interface AdminStats {
  totalUsers: number;
  totalListings: number;
  activeRentals: number;
  totalTransactions: number;
  pendingShipments: number;
  grossMerchandiseValue: number;
  totalCommission: number;
  totalProtectionFees: number;
  totalSellerPayouts: number;
}

// ─────────────────────────────────────────────────────────────────
// API CALLS
// ─────────────────────────────────────────────────────────────────

const COLLECTION_FIELDS = `
  id title subtitle description location region brand designer
  category style season price priceUnit spotsTotal spotsAvailable
  imageUrl galleryUrls videoUrl highlights includes featured status
  contactName contactEmail createdAt updatedAt
`;

const PRODUCT_FIELDS = `
  id name description price monthlyPrice price3 price24 price1 priceUnit category
  imageUrl galleryUrls size condition inStock sortOrder status createdAt updatedAt
`;

const WARDROBE_FIELDS = `
  id userId name description category size condition
  imageUrl galleryUrls imageVariants { originalUrl cutoutUrl useCutout }
  forRent rentPrice rentPrice3 rentPrice24 rentPrice1 forSale salePrice forSwap
  source sourceOrderId qregistryObjectId status weightTier moderationReason replacementValue
  dropId dropName dropReleaseAt dropStatus likeCount likedByMe isFeatured featuredAt featuredUntil sellerName createdAt updatedAt
  seller { type businessName orgNumber vatNumber address }
`;

const WARDROBE_ITEM_LIKE_FIELDS = `
  userId displayName avatarUrl createdAt
`;

const DROP_FIELDS = `
  id userId name releaseAt status createdAt updatedAt
  items { ${WARDROBE_FIELDS} }
`;

// Deliberately omits userId (and the items' userId) — user_id is the seller's
// email, and this fragment is used for the unauthenticated public share page.
const PUBLIC_DROP_FIELDS = `
  id name releaseAt status createdAt
  items { id name description category size condition imageUrl galleryUrls forRent rentPrice rentPrice3 rentPrice24 rentPrice1 forSale salePrice forSwap status }
`;

const SWAP_PROPOSAL_FIELDS = `
  myShipment { ${SHIPMENT_FIELDS} } incomingShipment { ${SHIPMENT_FIELDS} }
  id proposerUserId ownerUserId proposerName ownerName message ownerMessage durationMonths proposerSeenAt status createdAt updatedAt
  proposerDeliveryMethod ownerDeliveryMethod proposerShippingCost proposerProtectionFee ownerShippingCost
  proposerShippingPaidAt ownerShippingPaidAt proposerHandoverConfirmedAt ownerHandoverConfirmedAt proposerLegReceivedAt ownerLegReceivedAt problemReportedAt meetingAddress
  targetItem { ${WARDROBE_FIELDS} }
  offeredItems { ${WARDROBE_FIELDS} }
`;

const PURCHASE_OFFER_FIELDS = `
  id buyerUserId sellerUserId buyerName sellerName type offeredPrice bindingMonths message sellerMessage status createdAt updatedAt
  item { ${WARDROBE_FIELDS} }
`;

// Deliberately omits bustCm/waistCm/hipCm/bodyPhotos/notes — this fragment is used
// by the unauthenticated public sellerProfile query, so only avatar/bio are safe.
const PUBLIC_SELLER_PROFILE_FIELDS = `
  id userId displayName avatarUrl bio area { lat lng radiusM }
`;

const RETURN_BAG_FIELDS = `
  id userId bagCode status trackingNumber carrier wardrobeItemId
  orderedAt activatedAt shippedAt receivedAt createdAt updatedAt
`;

const TRANSACTION_FIELDS = `
  id wardrobeItemId sellerUserId buyerUserId type bindingMonths
  itemPrice commissionRate commissionAmount sellerPayout
  status returnBagId trackingNumber
  sellerTrackingNumber buyerReturnTracking
  shippedAt deliveredAt returnRequestedAt returnShippedAt returnReceivedAt
  itemName itemImageUrl deliveryMethod shippingCost shippingProvider shipment { ${SHIPMENT_FIELDS} } sellerHandoverConfirmedAt buyerHandoverConfirmedAt buyerCancelStatus buyerCancelRequestedAt buyerCancelReason
  payoutStatus protectionFee payoutDueAt problemReportedAt sellerName buyerName createdAt updatedAt
  rentalCharges { id monthIndex monthsTotal amount dueAt status paidAt payoutStatus } buyerCanCancel
`;

const ADMIN_STATS_FIELDS = `
  totalUsers totalListings activeRentals totalTransactions pendingShipments
  grossMerchandiseValue totalCommission totalProtectionFees totalSellerPayouts
`;

export type PushType = 'messages' | 'deals' | 'rentals' | 'support';
export interface PushSettings { disabledTypes: PushType[]; quietStart: number | null; quietEnd: number | null }

export const api = {
  // ── COLLECTIONS ──────────────────────────────────────────────────
  fashionCollections: (args?: { category?: FashionCategory; style?: FashionStyle; region?: string; season?: string }) =>
    gql<{ fashionCollections: FashionCollection[] }>(`
      query FashionCollections($category: FashionCategory, $style: FashionStyle, $region: String, $season: String) {
        fashionCollections(category: $category, style: $style, region: $region, season: $season) {
          ${COLLECTION_FIELDS}
        }
      }`, args ?? {}).then(d => d.fashionCollections),

  fashionCollection: (id: string) =>
    gql<{ fashionCollection: FashionCollection }>(`
      query FashionCollection($id: ID!) {
        fashionCollection(id: $id) { ${COLLECTION_FIELDS} }
      }`, { id }).then(d => d.fashionCollection),

  createFashionCollection: (input: Partial<FashionCollection>) =>
    gql<{ createFashionCollection: FashionCollection }>(`
      mutation CreateFashionCollection($input: CreateFashionCollectionInput!) {
        createFashionCollection(input: $input) { ${COLLECTION_FIELDS} }
      }`, { input }).then(d => d.createFashionCollection),

  updateFashionCollection: (id: string, input: Partial<FashionCollection>) =>
    gql<{ updateFashionCollection: FashionCollection }>(`
      mutation UpdateFashionCollection($id: ID!, $input: UpdateFashionCollectionInput!) {
        updateFashionCollection(id: $id, input: $input) { ${COLLECTION_FIELDS} }
      }`, { id, input }).then(d => d.updateFashionCollection),

  deleteFashionCollection: (id: string) =>
    gql<{ deleteFashionCollection: boolean }>(`
      mutation DeleteFashionCollection($id: ID!) {
        deleteFashionCollection(id: $id)
      }`, { id }).then(d => d.deleteFashionCollection),

  // ── PRODUCTS ───────────────────────────────────────────────────────
  products: (category?: string) =>
    gql<{ products: Product[] }>(`
      query Products($category: String) {
        products(category: $category) { ${PRODUCT_FIELDS} }
      }`, category ? { category } : {}).then(d => d.products),

  product: (id: string) =>
    gql<{ product: Product }>(`
      query Product($id: ID!) {
        product(id: $id) { ${PRODUCT_FIELDS} }
      }`, { id }).then(d => d.product),

  createProduct: (input: Partial<Product>) =>
    gql<{ createProduct: Product }>(`
      mutation CreateProduct($input: CreateProductInput!) {
        createProduct(input: $input) { ${PRODUCT_FIELDS} }
      }`, { input }).then(d => d.createProduct),

  updateProduct: (id: string, input: Partial<Product>) =>
    gql<{ updateProduct: Product }>(`
      mutation UpdateProduct($id: ID!, $input: UpdateProductInput!) {
        updateProduct(id: $id, input: $input) { ${PRODUCT_FIELDS} }
      }`, { id, input }).then(d => d.updateProduct),

  deleteProduct: (id: string) =>
    gql<{ deleteProduct: boolean }>(`
      mutation DeleteProduct($id: ID!) {
        deleteProduct(id: $id)
      }`, { id }).then(d => d.deleteProduct),

  // ── WARDROBE ──────────────────────────────────────────────────
  myWardrobe: () =>
    gql<{ myWardrobe: WardrobeItem[] }>(`
      query MyWardrobe { myWardrobe { ${WARDROBE_FIELDS} } }
    `).then(d => d.myWardrobe),

  marketplaceWardrobe: () =>
    gql<{ marketplaceWardrobe: WardrobeItem[] }>(`
      query MarketplaceWardrobe { marketplaceWardrobe { ${WARDROBE_FIELDS} } }
    `).then(d => d.marketplaceWardrobe),

  createWardrobeItem: (input: Partial<WardrobeItem>) =>
    gql<{ createWardrobeItem: WardrobeItem }>(`
      mutation CreateWardrobeItem($input: CreateWardrobeItemInput!) {
        createWardrobeItem(input: $input) { ${WARDROBE_FIELDS} }
      }`, { input }).then(d => d.createWardrobeItem),

  updateWardrobeItem: (id: string, input: Partial<WardrobeItem>) =>
    gql<{ updateWardrobeItem: WardrobeItem }>(`
      mutation UpdateWardrobeItem($id: ID!, $input: UpdateWardrobeItemInput!) {
        updateWardrobeItem(id: $id, input: $input) { ${WARDROBE_FIELDS} }
      }`, { id, input }).then(d => d.updateWardrobeItem),

  deleteWardrobeItem: (id: string) =>
    gql<{ deleteWardrobeItem: boolean }>(`
      mutation DeleteWardrobeItem($id: ID!) {
        deleteWardrobeItem(id: $id)
      }`, { id }).then(d => d.deleteWardrobeItem),

  // ── DROPS ─────────────────────────────────────────────────────
  myDrops: () =>
    gql<{ myDrops: Drop[] }>(`
      query MyDrops { myDrops { ${DROP_FIELDS} } }
    `).then(d => d.myDrops),

  createDrop: (input: { name: string; releaseAt: string; wardrobeItemIds: string[] }) =>
    gql<{ createDrop: Drop }>(`
      mutation CreateDrop($input: CreateDropInput!) {
        createDrop(input: $input) { ${DROP_FIELDS} }
      }`, { input }).then(d => d.createDrop),

  cancelDrop: (id: string) =>
    gql<{ cancelDrop: boolean }>(`
      mutation CancelDrop($id: ID!) {
        cancelDrop(id: $id)
      }`, { id }).then(d => d.cancelDrop),

  // Unauthenticated — used by the public /drops/:id share page.
  dropPublic: (id: string) =>
    gql<{ dropPublic: PublicDrop | null }>(`
      query DropPublic($id: ID!) {
        dropPublic(id: $id) { ${PUBLIC_DROP_FIELDS} }
      }`, { id }).then(d => d.dropPublic),

  // Unauthenticated — every non-cancelled drop across all sellers, for the "Kommande drops" tab.
  upcomingDrops: () =>
    gql<{ upcomingDrops: PublicDrop[] }>(`
      query UpcomingDrops { upcomingDrops { ${PUBLIC_DROP_FIELDS} } }
    `).then(d => d.upcomingDrops),

  // ── SWAP PROPOSALS ────────────────────────────────────────────
  mySwapProposalsSent: () =>
    gql<{ mySwapProposalsSent: SwapProposal[] }>(`
      query MySwapProposalsSent { mySwapProposalsSent { ${SWAP_PROPOSAL_FIELDS} } }
    `).then(d => d.mySwapProposalsSent),

  mySwapProposalsReceived: () =>
    gql<{ mySwapProposalsReceived: SwapProposal[] }>(`
      query MySwapProposalsReceived { mySwapProposalsReceived { ${SWAP_PROPOSAL_FIELDS} } }
    `).then(d => d.mySwapProposalsReceived),

  proposeSwap: (input: { targetItemId: string; offeredItemIds: string[]; message?: string; durationMonths?: number; deliveryMethod?: 'pickup' | 'shipping' }) =>
    gql<{ proposeSwap: SwapProposal }>(`
      mutation ProposeSwap($input: ProposeSwapInput!) {
        proposeSwap(input: $input) { ${SWAP_PROPOSAL_FIELDS} }
      }`, { input }).then(d => d.proposeSwap),

  respondToSwap: (id: string, accept: boolean, ownerMessage?: string, deliveryMethod?: 'pickup' | 'shipping') =>
    gql<{ respondToSwap: SwapProposal }>(`
      mutation RespondToSwap($id: ID!, $accept: Boolean!, $ownerMessage: String, $deliveryMethod: String) {
        respondToSwap(id: $id, accept: $accept, ownerMessage: $ownerMessage, deliveryMethod: $deliveryMethod) { ${SWAP_PROPOSAL_FIELDS} }
      }`, { id, accept, ownerMessage, deliveryMethod }).then(d => d.respondToSwap),

  cancelSwapProposal: (id: string) =>
    gql<{ cancelSwapProposal: boolean }>(`
      mutation CancelSwapProposal($id: ID!) {
        cancelSwapProposal(id: $id)
      }`, { id }).then(d => d.cancelSwapProposal),

  markSwapProposalSeen: (id: string) =>
    gql<{ markSwapProposalSeen: SwapProposal }>(`
      mutation MarkSwapProposalSeen($id: ID!) {
        markSwapProposalSeen(id: $id) { ${SWAP_PROPOSAL_FIELDS} }
      }`, { id }).then(d => d.markSwapProposalSeen),

  // Hämta (pickup)-bekräftelse för byten — se confirmSwapHandover i resolvers.ts.
  confirmSwapHandover: (id: string) =>
    gql<{ confirmSwapHandover: SwapProposal }>(`
      mutation ConfirmSwapHandover($id: ID!) {
        confirmSwapHandover(id: $id) { ${SWAP_PROPOSAL_FIELDS} }
      }`, { id }).then(d => d.confirmSwapHandover),

  // Frakt: mottagaren bekräftar att plaggen kommit, eller anmäler ett problem (api swaps/receipt.ts).
  confirmSwapReceived: (id: string) =>
    gql<{ confirmSwapReceived: SwapProposal }>(`
      mutation ConfirmSwapReceived($id: ID!) {
        confirmSwapReceived(id: $id) { ${SWAP_PROPOSAL_FIELDS} }
      }`, { id }).then(d => d.confirmSwapReceived),
  reportSwapProblem: (id: string, message: string) =>
    gql<{ reportSwapProblem: SwapProposal }>(`
      mutation ReportSwapProblem($id: ID!, $message: String!) {
        reportSwapProblem(id: $id, message: $message) { ${SWAP_PROPOSAL_FIELDS} }
      }`, { id, message }).then(d => d.reportSwapProblem),

  // ── PURCHASE OFFERS ───────────────────────────────────────────
  myOffersSent: () =>
    gql<{ myOffersSent: PurchaseOffer[] }>(`
      query MyOffersSent { myOffersSent { ${PURCHASE_OFFER_FIELDS} } }
    `).then(d => d.myOffersSent),

  myOffersReceived: () =>
    gql<{ myOffersReceived: PurchaseOffer[] }>(`
      query MyOffersReceived { myOffersReceived { ${PURCHASE_OFFER_FIELDS} } }
    `).then(d => d.myOffersReceived),

  proposeOffer: (input: { itemId: string; type: string; offeredPrice: number; bindingMonths?: number; message?: string }) =>
    gql<{ proposeOffer: PurchaseOffer }>(`
      mutation ProposeOffer($input: ProposeOfferInput!) {
        proposeOffer(input: $input) { ${PURCHASE_OFFER_FIELDS} }
      }`, { input }).then(d => d.proposeOffer),

  respondToOffer: (id: string, accept: boolean, sellerMessage?: string) =>
    gql<{ respondToOffer: PurchaseOffer }>(`
      mutation RespondToOffer($id: ID!, $accept: Boolean!, $sellerMessage: String) {
        respondToOffer(id: $id, accept: $accept, sellerMessage: $sellerMessage) { ${PURCHASE_OFFER_FIELDS} }
      }`, { id, accept, sellerMessage }).then(d => d.respondToOffer),

  cancelOffer: (id: string) =>
    gql<{ cancelOffer: boolean }>(`
      mutation CancelOffer($id: ID!) {
        cancelOffer(id: $id)
      }`, { id }).then(d => d.cancelOffer),

  // ── SELLER PROFILE (public, unauthenticated) ───────────────────
  sellerProfile: (userId: string) =>
    gql<{ sellerProfile: PublicSellerProfile | null }>(`
      query SellerProfile($userId: String!) {
        sellerProfile(userId: $userId) { ${PUBLIC_SELLER_PROFILE_FIELDS} }
      }`, { userId }).then(d => d.sellerProfile),

  wardrobeListing: (id: string) =>
    gql<{ wardrobeListing: WardrobeItem | null }>(`query WardrobeListing($id: ID!) { wardrobeListing(id: $id) { ${WARDROBE_FIELDS} } }`, { id }).then(d => d.wardrobeListing),

  // ── Användarvillkor (api/src/routes/legal.ts) — termsVersion null = inget krav än ──
  legalConfig: async (): Promise<{ termsVersion: string | null }> => {
    const res = await fetch(apiUrl('/api/legal/config')).catch(() => null);
    return res?.ok ? res.json() : { termsVersion: null };
  },
  legalStatus: async (): Promise<{ termsVersion: string | null; accepted: boolean }> => {
    const res = await fetch(apiUrl('/api/legal/status'), { headers: authHeaders() }).catch(() => null);
    return res?.ok ? res.json() : { termsVersion: null, accepted: true };
  },
  legalAccept: async (termsVersion: string): Promise<void> => {
    const res = await fetch(apiUrl('/api/legal/accept'), {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ termsVersion }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? 'Något gick fel');
  },

  crm: {
    leads: () => adminRest<CrmLead[]>('GET', '/api/admin/crm/leads'),
    lead: (id: string) => adminRest<CrmLeadDetail>('GET', `/api/admin/crm/leads/${id}`),
    create: (input: Partial<CrmLead>) => adminRest<CrmLeadDetail>('POST', '/api/admin/crm/leads', input),
    update: (id: string, patch: Partial<CrmLead>) => adminRest<CrmLeadDetail>('PATCH', `/api/admin/crm/leads/${id}`, patch),
    remove: (id: string) => fetch(apiUrl(`/api/admin/crm/leads/${id}`), { method: 'DELETE', headers: authHeaders() }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); }),
    addNote: (id: string, kind: CrmNote['kind'], body: string) => adminRest<CrmLeadDetail>('POST', `/api/admin/crm/leads/${id}/notes`, { kind, body }),
    deleteNote: (id: string, noteId: string) => fetch(apiUrl(`/api/admin/crm/leads/${id}/notes/${noteId}`), { method: 'DELETE', headers: authHeaders() }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); }),
    previewImport: (text: string) => adminRest<{ rows: CrmImportRow[] }>('POST', '/api/admin/crm/import/preview', { text }),
    import: (text: string, owner?: string, priority?: string) => adminRest<{ inserted: number; skipped: number }>('POST', '/api/admin/crm/import', { text, owner, priority }),
  },

  marketing: {
    campaigns: () => adminRest<MarketingCampaign[]>('GET', '/api/admin/marketing/campaigns'),
    campaign: (id: string) => adminRest<MarketingCampaignDetail>('GET', `/api/admin/marketing/campaigns/${id}`),
    createCampaign: (input: Partial<MarketingCampaign>) => adminRest<MarketingCampaignDetail>('POST', '/api/admin/marketing/campaigns', input),
    createLaunchTest: () => adminRest<MarketingCampaignDetail>('POST', '/api/admin/marketing/campaigns/launch-test'),
    updateCampaign: (id: string, patch: Partial<MarketingCampaign>) => adminRest<MarketingCampaignDetail>('PATCH', `/api/admin/marketing/campaigns/${id}`, patch),
    deleteCampaign: (id: string) => fetch(apiUrl(`/api/admin/marketing/campaigns/${id}`), { method: 'DELETE', headers: authHeaders() }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); }),
    results: (id: string) => adminRest<MarketingResults>('GET', `/api/admin/marketing/campaigns/${id}/results`),
    createCreative: (campaignId: string, input: Partial<MarketingCreative>) => adminRest<MarketingCampaignDetail>('POST', `/api/admin/marketing/campaigns/${campaignId}/creatives`, input),
    updateCreative: (id: string, patch: Partial<MarketingCreative>) => adminRest<MarketingCampaignDetail>('PATCH', `/api/admin/marketing/creatives/${id}`, patch),
    deleteCreative: (id: string) => adminRest<MarketingCampaignDetail>('DELETE', `/api/admin/marketing/creatives/${id}`),
    platforms: () => adminRest<MarketingPlatformStatus[]>('GET', '/api/admin/marketing/platforms'),
    sync: () => adminRest<{ results: { platform: MarketingPlatform; rows?: number; error?: string; skipped?: boolean }[] }>('POST', '/api/admin/marketing/sync', {}),
    publish: (campaignId: string, platform: MarketingPlatform) => adminRest<{ created: string[]; skipped: string[] }>('POST', `/api/admin/marketing/campaigns/${campaignId}/publish/${platform}`, {}),
    setRunning: (campaignId: string, platform: MarketingPlatform, running: boolean) => adminRest<MarketingCampaignDetail>('POST', `/api/admin/marketing/campaigns/${campaignId}/${platform}/${running ? 'start' : 'pause'}`, {}),
    setAdRunning: (creativeId: string, platform: MarketingPlatform, running: boolean) => adminRest<MarketingCampaignDetail>('POST', `/api/admin/marketing/creatives/${creativeId}/${platform}/${running ? 'start' : 'pause'}`, {}),
  },

  // ── Frakt via Fraktjakt: fraktsätt till verkligt pris (api/src/shipping/quotes.ts) ──
  shippingFixedPrice: (): Promise<number | null> =>
    gql<{ shippingFixedPrice: number | null }>(`query { shippingFixedPrice }`).then(d => d.shippingFixedPrice).catch(() => null),
  fraktjaktEnabled: (): Promise<boolean> =>
    gql<{ fraktjaktEnabled: boolean }>(`query { fraktjaktEnabled }`).then(d => d.fraktjaktEnabled).catch(() => false),
  shippingOptions: (itemId: string, offerId?: string) =>
    gql<{ shippingOptions: ShippingOption[] }>(`query ShippingOptions($itemId: ID!, $offerId: ID) { shippingOptions(itemId: $itemId, offerId: $offerId) { ${SHIPPING_OPTION_FIELDS} } }`, { itemId, offerId })
      .then(d => d.shippingOptions),
  returnShippingOptions: (returnId: string) =>
    gql<{ returnShippingOptions: ShippingOption[] }>(`query ReturnShippingOptions($returnId: ID!) { returnShippingOptions(returnId: $returnId) { ${SHIPPING_OPTION_FIELDS} } }`, { returnId })
      .then(d => d.returnShippingOptions),
  swapShippingOptions: (proposalId: string) =>
    gql<{ swapShippingOptions: ShippingOption[] }>(`query SwapShippingOptions($proposalId: ID!) { swapShippingOptions(proposalId: $proposalId) { ${SHIPPING_OPTION_FIELDS} } }`, { proposalId })
      .then(d => d.swapShippingOptions),

  // ── Hyresförsäkring via Omocom (api/src/insurance/rentalInsurance.ts) ──
  insuranceAvailable: async (): Promise<boolean> => {
    const res = await fetch(apiUrl('/api/insurance/available')).catch(() => null);
    return !!res?.ok && !!(await res.json().catch(() => ({})))?.available;
  },
  insuranceQuote: async (wardrobeItemId: string, bindingMonths: number): Promise<{ id: string; premium: number; insuredAmount: number; bindingMonths: number }> => {
    const res = await fetch(apiUrl('/api/insurance/quote'), {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ wardrobeItemId, bindingMonths }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.insuranceQuoteFailed'));
    return data;
  },

  // ── OAuth-godkännande för MCP-kopplingar (api/src/mcp/oauth.ts) ──
  oauthRequest: async (id: string): Promise<{ clientName: string; redirectHost: string }> => {
    const res = await fetch(apiUrl(`/oauth/request/${encodeURIComponent(id)}`));
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.requestNotFound'));
    return data;
  },
  oauthDecide: async (requestId: string, approve: boolean): Promise<string> => {
    const res = await fetch(apiUrl(approve ? '/oauth/approve' : '/oauth/deny'), {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ requestId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.redirectUrl) throw new Error(data?.error ?? t('common.somethingWentWrong'));
    return data.redirectUrl;
  },

  // ── Åtkomsttokens för agenter (MCP, se api/src/mcp/) ──
  // Pushnotiser (lib/push.ts, api/src/routes/push.ts)
  registerPushToken: async (token: string): Promise<void> => {
    const res = await fetch(apiUrl('/api/push/register'), {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ token, platform: Capacitor.getPlatform() === 'android' ? 'android' : 'ios' }),
    });
    if (!res.ok) throw new Error('push register');
  },
  unregisterPushToken: async (token: string): Promise<void> => {
    await fetch(apiUrl('/api/push/register'), {
      method: 'DELETE', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ token }),
    });
  },
  getPushSettings: async (): Promise<PushSettings> => {
    const res = await fetch(apiUrl('/api/push/settings'), { headers: authHeaders() });
    if (!res.ok) throw new Error(t('common.somethingWentWrong'));
    return res.json();
  },
  savePushSettings: async (settings: PushSettings): Promise<PushSettings> => {
    const res = await fetch(apiUrl('/api/push/settings'), {
      method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(settings),
    });
    if (!res.ok) throw new Error(t('common.somethingWentWrong'));
    return res.json();
  },

  myApiTokens: () =>
    gql<{ myApiTokens: ApiToken[] }>(`query { myApiTokens { id name prefix createdAt lastUsedAt } }`).then(d => d.myApiTokens),
  createApiToken: (name: string) =>
    gql<{ createApiToken: { token: string; apiToken: ApiToken } }>(`mutation CreateApiToken($name: String!) { createApiToken(name: $name) { token apiToken { id name prefix createdAt lastUsedAt } } }`, { name }).then(d => d.createApiToken),
  revokeApiToken: (id: string) =>
    gql<{ revokeApiToken: boolean }>(`mutation RevokeApiToken($id: ID!) { revokeApiToken(id: $id) }`, { id }).then(d => d.revokeApiToken),

  sellerAreas: () =>
    gql<{ sellerAreas: SellerArea[] }>(`query { sellerAreas { userId area { lat lng radiusM } } }`).then(d => d.sellerAreas),

  sellerWardrobe: (userId: string) =>
    gql<{ sellerWardrobe: WardrobeItem[] }>(`
      query SellerWardrobe($userId: String!) {
        sellerWardrobe(userId: $userId) { ${WARDROBE_FIELDS} }
      }`, { userId }).then(d => d.sellerWardrobe),

  // ── LIKES ─────────────────────────────────────────────────────
  myLikedItems: () =>
    gql<{ myLikedItems: WardrobeItem[] }>(`
      query MyLikedItems { myLikedItems { ${WARDROBE_FIELDS} } }
    `).then(d => d.myLikedItems),

  wardrobeItemLikers: (itemId: string) =>
    gql<{ wardrobeItemLikers: WardrobeItemLike[] }>(`
      query WardrobeItemLikers($itemId: ID!) {
        wardrobeItemLikers(itemId: $itemId) { ${WARDROBE_ITEM_LIKE_FIELDS} }
      }`, { itemId }).then(d => d.wardrobeItemLikers),

  likeWardrobeItem: (itemId: string) =>
    gql<{ likeWardrobeItem: WardrobeItem }>(`
      mutation LikeWardrobeItem($itemId: ID!) {
        likeWardrobeItem(itemId: $itemId) { ${WARDROBE_FIELDS} }
      }`, { itemId }).then(d => d.likeWardrobeItem),

  unlikeWardrobeItem: (itemId: string) =>
    gql<{ unlikeWardrobeItem: WardrobeItem }>(`
      mutation UnlikeWardrobeItem($itemId: ID!) {
        unlikeWardrobeItem(itemId: $itemId) { ${WARDROBE_FIELDS} }
      }`, { itemId }).then(d => d.unlikeWardrobeItem),

  // ── RETURN BAGS ───────────────────────────────────────────────
  myReturnBags: () =>
    gql<{ myReturnBags: ReturnBag[] }>(`
      query MyReturnBags { myReturnBags { ${RETURN_BAG_FIELDS} } }
    `).then(d => d.myReturnBags),

  orderReturnBag: (wardrobeItemId?: string) =>
    gql<{ orderReturnBag: ReturnBag }>(`
      mutation OrderReturnBag($wardrobeItemId: ID) {
        orderReturnBag(wardrobeItemId: $wardrobeItemId) { ${RETURN_BAG_FIELDS} }
      }`, { wardrobeItemId }).then(d => d.orderReturnBag),

  activateReturnBag: (bagCode: string) =>
    gql<{ activateReturnBag: ReturnBag }>(`
      mutation ActivateReturnBag($bagCode: String!) {
        activateReturnBag(bagCode: $bagCode) { ${RETURN_BAG_FIELDS} }
      }`, { bagCode }).then(d => d.activateReturnBag),

  shipReturnBag: (bagId: string, trackingNumber: string) =>
    gql<{ shipReturnBag: ReturnBag }>(`
      mutation ShipReturnBag($bagId: ID!, $trackingNumber: String!) {
        shipReturnBag(bagId: $bagId, trackingNumber: $trackingNumber) { ${RETURN_BAG_FIELDS} }
      }`, { bagId, trackingNumber }).then(d => d.shipReturnBag),

  // ── TRANSACTIONS ──────────────────────────────────────────────
  myTransactions: () =>
    gql<{ myTransactions: WardrobeTransaction[] }>(`
      query MyTransactions { myTransactions { ${TRANSACTION_FIELDS} } }
    `).then(d => d.myTransactions),

  mySales: () =>
    gql<{ mySales: WardrobeTransaction[] }>(`
      query MySales { mySales { ${TRANSACTION_FIELDS} } }
    `).then(d => d.mySales),

  // ── ADMIN ─────────────────────────────────────────────────────
  adminStats: () =>
    gql<{ adminStats: AdminStats }>(`
      query AdminStats { adminStats { ${ADMIN_STATS_FIELDS} } }
    `).then(d => d.adminStats),

  adminTransactions: (args: { from?: string; to?: string; status?: string } = {}) =>
    gql<{ adminTransactions: WardrobeTransaction[] }>(`
      query AdminTransactions($from: String, $to: String, $status: String) {
        adminTransactions(from: $from, to: $to, status: $status) { ${TRANSACTION_FIELDS} }
      }`, args).then(d => d.adminTransactions),

  // ── FEATURED ───────────────────────────────────────────────────
  featurePack: () =>
    gql<{ featurePack: FeaturePack }>(`query { featurePack { items days pricePerItemWeek price } }`).then(d => d.featurePack),

  myFeatureCredits: () =>
    gql<{ myFeatureCredits: number }>(`query { myFeatureCredits }`).then(d => d.myFeatureCredits),

  featureMyItem: (itemId: string) =>
    gql<{ featureMyItem: WardrobeItem }>(`
      mutation FeatureMyItem($itemId: ID!) { featureMyItem(itemId: $itemId) { ${WARDROBE_FIELDS} } }`, { itemId }).then(d => d.featureMyItem),

  /** Returnerar Stripe Checkout-url. */
  buyFeaturePack: (itemId?: string) =>
    gql<{ buyFeaturePack: string }>(`
      mutation BuyFeaturePack($itemId: ID) { buyFeaturePack(itemId: $itemId) }`, { itemId }).then(d => d.buyFeaturePack),

  adminSetItemFeatured: (itemId: string, featured: boolean) =>
    gql<{ adminSetItemFeatured: WardrobeItem }>(`
      mutation AdminSetItemFeatured($itemId: ID!, $featured: Boolean!) { adminSetItemFeatured(itemId: $itemId, featured: $featured) { ${WARDROBE_FIELDS} } }`, { itemId, featured }).then(d => d.adminSetItemFeatured),

  // ── BETYG ──────────────────────────────────────────────────────
  userReputation: (userId: string) =>
    gql<{ userReputation: UserReputation }>(`
      query UserReputation($userId: String!) {
        userReputation(userId: $userId) {
          userId averageRating reviewCount buyCount rentCount swapCount memberSince
          reviews { id rating comment kind reviewerName createdAt }
        }
      }`, { userId }).then(d => d.userReputation),

  myPendingReviews: () =>
    gql<{ myPendingReviews: PendingReview[] }>(`query { myPendingReviews {
      sourceType sourceId kind counterpartyUserId counterpartyName itemName itemImageUrl completedAt
    } }`).then(d => d.myPendingReviews),

  submitReview: (sourceType: PendingReview['sourceType'], sourceId: string, rating: number, comment?: string) =>
    gql<{ submitReview: UserReview }>(`
      mutation SubmitReview($sourceType: String!, $sourceId: ID!, $rating: Int!, $comment: String) {
        submitReview(sourceType: $sourceType, sourceId: $sourceId, rating: $rating, comment: $comment) { id rating comment kind reviewerName createdAt }
      }`, { sourceType, sourceId, rating, comment }).then(d => d.submitReview),

  myReturns: () =>
    gql<{ myReturns: ItemReturn[] }>(`query { myReturns { ${RETURN_FIELDS} } }`).then(d => d.myReturns),

  startItemReturn: (id: string, method: 'dropoff' | 'shipping', photos: string[]) =>
    gql(`mutation StartItemReturn($id: ID!, $method: String!, $photos: [String!]!) { startItemReturn(id: $id, method: $method, photos: $photos) }`, { id, method, photos }),

  reviewItemReturn: (id: string, verdict: string, note?: string, itemValue?: number) =>
    gql(`mutation ReviewItemReturn($id: ID!, $verdict: String!, $note: String, $itemValue: Float) { reviewItemReturn(id: $id, verdict: $verdict, note: $note, itemValue: $itemValue) }`, { id, verdict, note, itemValue }),

  decideReturnCompensation: (id: string, accept: boolean) =>
    gql(`mutation DecideReturnCompensation($id: ID!, $accept: Boolean!) { decideReturnCompensation(id: $id, accept: $accept) }`, { id, accept }),

  markItemReturnHandedOver: (id: string) =>
    gql(`mutation MarkItemReturnHandedOver($id: ID!) { markItemReturnHandedOver(id: $id) }`, { id }),

  confirmItemReturnReceived: (id: string) =>
    gql(`mutation ConfirmItemReturnReceived($id: ID!) { confirmItemReturnReceived(id: $id) }`, { id }),

  // Stripe Checkout för kompensation eller returfrakt — ger adressen att skicka användaren till.
  returnPaymentSession: async (returnId: string, purpose: 'compensation' | 'shipping', quoteId?: string): Promise<string> => {
    const res = await fetch(apiUrl('/checkout/return-session'), {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnId, purpose, quoteId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.sessionUrl) throw new Error(data?.error ?? t('api.paymentStartFailed'));
    return data.sessionUrl;
  },

  myConversations: () =>
    gql<{ myConversations: Conversation[] }>(`query { myConversations {
      id unreadCount blockedByMe updatedAt otherUser { id displayName avatarUrl }
      lastMessage { id conversationId mine body itemId item { id name imageUrl } flaggedReason createdAt } item { id name imageUrl }
    } }`).then(d => d.myConversations),

  conversationMessages: (conversationId: string) =>
    gql<{ conversationMessages: ChatMessage[] }>(`
      query ConversationMessages($conversationId: ID!) { conversationMessages(conversationId: $conversationId) { id conversationId mine body itemId item { id name imageUrl } flaggedReason createdAt } }`,
      { conversationId }).then(d => d.conversationMessages),

  unreadMessageCount: () =>
    gql<{ unreadMessageCount: number }>(`query { unreadMessageCount }`).then(d => d.unreadMessageCount),

  sendMessage: (args: { toUserId?: string; conversationId?: string; body: string; itemId?: string }) =>
    gql<{ sendMessage: ChatMessage }>(`
      mutation SendMessage($toUserId: String, $conversationId: ID, $body: String!, $itemId: ID) {
        sendMessage(toUserId: $toUserId, conversationId: $conversationId, body: $body, itemId: $itemId) { id conversationId mine body itemId item { id name imageUrl } flaggedReason createdAt }
      }`, args).then(d => d.sendMessage),

  // Översätter ett meddelande till valt gränssnittsspråk (cachas på servern).
  translateMessage: (messageId: string) =>
    gql<{ translateMessage: string }>(`
      mutation TranslateMessage($messageId: ID!, $lang: String!) {
        translateMessage(messageId: $messageId, lang: $lang)
      }`, { messageId, lang: getLang() }).then(d => d.translateMessage),

  markConversationRead: (conversationId: string) =>
    gql<{ markConversationRead: boolean }>(`
      mutation MarkRead($conversationId: ID!) { markConversationRead(conversationId: $conversationId) }`, { conversationId }),

  setMessageBlock: (userId: string, blocked: boolean) =>
    gql<{ setMessageBlock: boolean }>(`
      mutation SetMessageBlock($userId: String!, $blocked: Boolean!) { setMessageBlock(userId: $userId, blocked: $blocked) }`, { userId, blocked }),

  supportCategories: () =>
    gql<{ supportCategories: { report: string[]; support: string[] } }>(`query { supportCategories { report support } }`).then(d => d.supportCategories),

  submitSupportTicket: (input: SupportTicketInput) =>
    gql<{ submitSupportTicket: { number: string } }>(`
      mutation SubmitSupportTicket($input: SupportTicketInput!) { submitSupportTicket(input: $input) { number } }`,
      { input }).then(d => d.submitSupportTicket),

  adminTickets: (status?: string) =>
    gql<{ adminTickets: SupportTicket[] }>(`
      query AdminTickets($status: String) { adminTickets(status: $status) { id number kind category status reporterEmail reporterName reporterUserId targetType targetId targetName targetStatus message resolution createdAt updatedAt closedAt messages { id author authorEmail body internal createdAt } ${AGENT_TASK_FIELDS} } }`,
      { status: status || undefined }).then(d => d.adminTickets),

  adminOpenTicketCount: () =>
    gql<{ adminOpenTicketCount: number }>(`query { adminOpenTicketCount }`).then(d => d.adminOpenTicketCount),

  adminReplyTicket: (id: string, body: string, internal: boolean, agentTaskId?: string) =>
    gql<{ adminReplyTicket: SupportTicket }>(`
      mutation AdminReplyTicket($id: ID!, $body: String!, $internal: Boolean, $agentTaskId: ID) { adminReplyTicket(id: $id, body: $body, internal: $internal, agentTaskId: $agentTaskId) { id number kind category status reporterEmail reporterName reporterUserId targetType targetId targetName targetStatus message resolution createdAt updatedAt closedAt messages { id author authorEmail body internal createdAt } ${AGENT_TASK_FIELDS} } }`,
      { id, body, internal, agentTaskId }).then(d => d.adminReplyTicket),

  adminRejectAgentTask: (id: string) =>
    gql<{ adminRejectAgentTask: SupportTicket }>(`
      mutation AdminRejectAgentTask($id: ID!) { adminRejectAgentTask(id: $id) { ${TICKET_FIELDS} } }`, { id }).then(d => d.adminRejectAgentTask),

  adminRequeueAgentTask: (id: string) =>
    gql<{ adminRequeueAgentTask: SupportTicket }>(`
      mutation AdminRequeueAgentTask($id: ID!) { adminRequeueAgentTask(id: $id) { ${TICKET_FIELDS} } }`, { id }).then(d => d.adminRequeueAgentTask),

  adminSetTicketStatus: (id: string, status: SupportTicket['status'], resolution?: string) =>
    gql<{ adminSetTicketStatus: SupportTicket }>(`
      mutation AdminSetTicketStatus($id: ID!, $status: String!, $resolution: String) { adminSetTicketStatus(id: $id, status: $status, resolution: $resolution) { id number kind category status reporterEmail reporterName reporterUserId targetType targetId targetName targetStatus message resolution createdAt updatedAt closedAt messages { id author authorEmail body internal createdAt } ${AGENT_TASK_FIELDS} } }`,
      { id, status, resolution }).then(d => d.adminSetTicketStatus),

  adminIssues: () =>
    gql<{ adminIssues: AdminIssue[] }>(`
      query { adminIssues { kind message since transaction { ${TRANSACTION_FIELDS} } } }`).then(d => d.adminIssues),

  adminSearchListings: (query: string, includeRemoved: boolean) =>
    gql<{ adminSearchListings: WardrobeItem[] }>(`
      query AdminSearchListings($query: String, $includeRemoved: Boolean) {
        adminSearchListings(query: $query, includeRemoved: $includeRemoved) { ${WARDROBE_FIELDS} }
      }`, { query, includeRemoved }).then(d => d.adminSearchListings),

  adminBlockedUsers: () =>
    gql<{ adminBlockedUsers: BlockedUser[] }>(`query { adminBlockedUsers { userId reason blockedBy createdAt } }`).then(d => d.adminBlockedUsers),

  adminActionLog: (limit = 50) =>
    gql<{ adminActionLog: AdminAction[] }>(`
      query AdminActionLog($limit: Int) { adminActionLog(limit: $limit) { id adminUserId action targetType targetId reason createdAt } }`,
      { limit }).then(d => d.adminActionLog),

  adminRemoveListing: (itemId: string, reason: string) =>
    gql<{ adminRemoveListing: WardrobeItem }>(`
      mutation AdminRemoveListing($itemId: ID!, $reason: String!) { adminRemoveListing(itemId: $itemId, reason: $reason) { ${WARDROBE_FIELDS} } }`,
      { itemId, reason }).then(d => d.adminRemoveListing),

  adminRestoreListing: (itemId: string) =>
    gql<{ adminRestoreListing: WardrobeItem }>(`
      mutation AdminRestoreListing($itemId: ID!) { adminRestoreListing(itemId: $itemId) { ${WARDROBE_FIELDS} } }`,
      { itemId }).then(d => d.adminRestoreListing),

  adminBlockUser: (userId: string, reason: string) =>
    gql<{ adminBlockUser: BlockedUser }>(`
      mutation AdminBlockUser($userId: String!, $reason: String!) { adminBlockUser(userId: $userId, reason: $reason) { userId reason blockedBy createdAt } }`,
      { userId, reason }).then(d => d.adminBlockUser),

  adminUnblockUser: (userId: string) =>
    gql<{ adminUnblockUser: boolean }>(`
      mutation AdminUnblockUser($userId: String!) { adminUnblockUser(userId: $userId) }`, { userId }).then(d => d.adminUnblockUser),

  adminCancelTransaction: (transactionId: string, reason: string) =>
    gql<{ adminCancelTransaction: AdminActionResult }>(`
      mutation AdminCancelTransaction($transactionId: ID!, $reason: String!) { adminCancelTransaction(transactionId: $transactionId, reason: $reason) { ok message } }`,
      { transactionId, reason }).then(d => d.adminCancelTransaction),

  adminRetryPayout: (transactionId: string) =>
    gql<{ adminRetryPayout: AdminActionResult }>(`
      mutation AdminRetryPayout($transactionId: ID!) { adminRetryPayout(transactionId: $transactionId) { ok message } }`,
      { transactionId }).then(d => d.adminRetryPayout),

  shipToBuyer: (transactionId: string, trackingNumber: string, carrier?: string) =>
    gql<{ shipToBuyer: WardrobeTransaction }>(`
      mutation ShipToBuyer($transactionId: ID!, $trackingNumber: String!, $carrier: String) {
        shipToBuyer(transactionId: $transactionId, trackingNumber: $trackingNumber, carrier: $carrier) { ${TRANSACTION_FIELDS} }
      }`, { transactionId, trackingNumber, carrier }).then(d => d.shipToBuyer),

  // Månad som inte gick att dra från kortet → Stripe Checkout (api/src/rentals/billing.ts)
  rentChargePaymentSession: async (chargeId: string): Promise<string> => {
    const res = await fetch(apiUrl('/checkout/rent-charge-session'), {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ chargeId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.sessionUrl) throw new Error(data?.error ?? 'Kunde inte starta betalningen');
    return data.sessionUrl;
  },

  cancelMyPurchase: (transactionId: string, reason?: string) =>
    gql<{ cancelMyPurchase: { refunded: number | null; requested: boolean } }>(`
      mutation CancelMyPurchase($transactionId: ID!, $reason: String) {
        cancelMyPurchase(transactionId: $transactionId, reason: $reason) { refunded requested }
      }`, { transactionId, reason }).then(d => d.cancelMyPurchase),

  respondToBuyerCancel: (transactionId: string, approve: boolean, message?: string) =>
    gql<{ respondToBuyerCancel: { refunded: number | null; ticketNumber: string | null } }>(`
      mutation RespondToBuyerCancel($transactionId: ID!, $approve: Boolean!, $message: String) {
        respondToBuyerCancel(transactionId: $transactionId, approve: $approve, message: $message) { refunded ticketNumber }
      }`, { transactionId, approve, message }).then(d => d.respondToBuyerCancel),

  reportTransactionProblem: (transactionId: string, message: string) =>
    gql<{ reportTransactionProblem: { ticketNumber: string } }>(`
      mutation ReportTransactionProblem($transactionId: ID!, $message: String!) {
        reportTransactionProblem(transactionId: $transactionId, message: $message) { ticketNumber }
      }`, { transactionId, message }).then(d => d.reportTransactionProblem),

  confirmDelivery: (transactionId: string) =>
    gql<{ confirmDelivery: WardrobeTransaction }>(`
      mutation ConfirmDelivery($transactionId: ID!) {
        confirmDelivery(transactionId: $transactionId) { ${TRANSACTION_FIELDS} }
      }`, { transactionId }).then(d => d.confirmDelivery),

  // Hämta (pickup)-bekräftelse — se confirmHandover i resolvers.ts. Kräver
  // att BÅDA säljare och köpare anropar den innan status blir 'delivered'.
  confirmHandover: (transactionId: string) =>
    gql<{ confirmHandover: WardrobeTransaction }>(`
      mutation ConfirmHandover($transactionId: ID!) {
        confirmHandover(transactionId: $transactionId) { ${TRANSACTION_FIELDS} }
      }`, { transactionId }).then(d => d.confirmHandover),

  requestReturn: (transactionId: string) =>
    gql<{ requestReturn: WardrobeTransaction }>(`
      mutation RequestReturn($transactionId: ID!) {
        requestReturn(transactionId: $transactionId) { ${TRANSACTION_FIELDS} }
      }`, { transactionId }).then(d => d.requestReturn),

  shipReturn: (transactionId: string, trackingNumber: string) =>
    gql<{ shipReturn: WardrobeTransaction }>(`
      mutation ShipReturn($transactionId: ID!, $trackingNumber: String!) {
        shipReturn(transactionId: $transactionId, trackingNumber: $trackingNumber) { ${TRANSACTION_FIELDS} }
      }`, { transactionId, trackingNumber }).then(d => d.shipReturn),

  confirmReturnReceived: (transactionId: string) =>
    gql<{ confirmReturnReceived: WardrobeTransaction }>(`
      mutation ConfirmReturnReceived($transactionId: ID!) {
        confirmReturnReceived(transactionId: $transactionId) { ${TRANSACTION_FIELDS} }
      }`, { transactionId }).then(d => d.confirmReturnReceived),

  // ── BOOKINGS ─────────────────────────────────────────────────────
  createFashionBooking: (input: { collectionId: string; customerName: string; customerEmail: string; customerPhone?: string; size?: string; notes?: string }) =>
    gql<{ createFashionBooking: FashionBooking }>(`
      mutation CreateFashionBooking($input: CreateFashionBookingInput!) {
        createFashionBooking(input: $input) {
          id collectionId customerName customerEmail size status createdAt
        }
      }`, { input }).then(d => d.createFashionBooking),

  // ── PROFILE (REST) ───────────────────────────────────────────────
  fetchProfile: async (): Promise<FashionProfile | null> => {
    const token = getAccessToken();
    if (!token) return null;
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      const idToken = localStorage.getItem('qvestium_id_token');
      if (idToken) headers['X-Id-Token'] = idToken;
      const res = await fetch(apiUrl('/api/profile'), { headers });
      if (!res.ok) return null;
      return absolutizeUploadUrls(await res.json() as FashionProfile);
    } catch { return null; }
  },

  // ── Skatteuppgifter för DAC7 (api/src/account/dac7.ts) ──
  taxInfoStatus: async (): Promise<TaxInfoStatus> => {
    const res = await fetch(apiUrl('/api/tax-info'), { headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? 'Något gick fel');
    return data;
  },
  saveTaxInfo: async (body: Record<string, string | undefined>): Promise<TaxInfoStatus> => {
    const res = await fetch(apiUrl('/api/tax-info'), { method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? 'Kunde inte spara');
    return data;
  },
  downloadDac7Report: async (year: number): Promise<void> => {
    const res = await fetch(apiUrl(`/api/admin/dac7.csv?year=${year}`), { headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? 'Kunde inte hämta underlaget');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await res.blob()); a.download = `qvestium-dac7-${year}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  },

  // ── Mina uppgifter: export och radering (api/src/account/dataRights.ts) ──
  exportMyData: async (): Promise<void> => {
    if (IS_NATIVE_APP) {
      // Webbvyn i appen kan inte spara en nedladdning: en engångslänk (5 min)
      // öppnas i webbläsaren – API:ts domän ligger utanför allowNavigation
      // (capacitor.config.ts), så Capacitor skickar den till Safari/Chrome.
      const link = await fetch(apiUrl('/api/account/export-link'), { method: 'POST', headers: authHeaders() });
      const data = await link.json().catch(() => ({}));
      if (!link.ok) throw new Error(data?.error ?? 'Kunde inte hämta uppgifterna');
      window.location.href = apiUrl(data.path);
      return;
    }
    const res = await fetch(apiUrl('/api/account/export'), { headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? 'Kunde inte hämta uppgifterna');
    const blob = await res.blob();
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'qvestium-mina-uppgifter.json';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  },
  accountDeletionCheck: async (): Promise<{ canDelete: boolean; blockers: string[] }> => {
    const res = await fetch(apiUrl('/api/account/deletion-check'), { headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? 'Något gick fel');
    return data;
  },
  deleteMyAccount: async (): Promise<void> => {
    const res = await fetch(apiUrl('/api/account/delete'), { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ confirm: 'RADERA' }) });
    if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? 'Kontot kunde inte raderas');
  },

  // Som updateProfile, men kastar med serverns felmeddelande (validering av säljartyp m.m.).
  saveProfileStrict: async (data: Record<string, unknown>): Promise<FashionProfile> => {
    const res = await fetch(apiUrl('/api/profile'), { method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error ?? 'Kunde inte spara');
    return absolutizeUploadUrls(json as FashionProfile);
  },

  updateProfile: async (data: Partial<FashionProfile>): Promise<FashionProfile | null> => {
    const token = getAccessToken();
    if (!token) return null;
    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      const idToken = localStorage.getItem('qvestium_id_token');
      if (idToken) headers['X-Id-Token'] = idToken;
      const res = await fetch(apiUrl('/api/profile'), {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) return null;
      return absolutizeUploadUrls(await res.json() as FashionProfile);
    } catch { return null; }
  },

  // ── STRIPE CONNECT (REST) ─────────────────────────────────────────
  // Säljares utbetalningskonto — förutsättning för att få betalt automatiskt
  // när ett köp/hyra bekräftas levererat (se payments/releaseOnDelivery.ts).
  connectStatus: async (): Promise<{ onboarded: boolean; chargesEnabled: boolean; payoutsEnabled: boolean } | null> => {
    const token = getAccessToken();
    if (!token) return null;
    try {
      const res = await fetch(apiUrl('/connect/status'), { headers: authHeaders() });
      if (!res.ok) return null;
      return res.json();
    } catch { return null; }
  },

  // ── IDENTITET (BankID via Qid/Idura) ───────────────────────────────
  identityStatus: async (): Promise<IdentityStatus | null> => {
    if (!getAccessToken()) return null;
    try {
      const res = await fetch(apiUrl('/api/identity/status'), { headers: authHeaders() });
      return res.ok ? res.json() : null;
    } catch { return null; }
  },

  identityStart: async (): Promise<{ url?: string; already_verified?: boolean }> => {
    const res = await fetch(apiUrl('/api/identity/start'), { method: 'POST', headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.verificationStartFailed'));
    return data;
  },

  connectOnboard: async (): Promise<{ url: string } | null> => {
    const token = getAccessToken();
    if (!token) return null;
    try {
      const res = await fetch(apiUrl('/connect/onboard'), { method: 'POST', headers: authHeaders() });
      if (!res.ok) return null;
      return res.json();
    } catch { return null; }
  },

  // ── SHIPPING (REST, admin) ─────────────────────────────────────────
  // Manuell Bring-koll för en enskild sändning — förbigår avstämningsjobbets
  // 30-min-intervall och "väntat till förväntad leverans"-spärr, se
  // routes/shipping.ts. Enbart för admin-testning.
  checkShipmentNow: async (transactionId: string): Promise<{ checked: boolean; found: boolean; result?: unknown }> => {
    const token = getAccessToken();
    if (!token) throw new Error(t('common.authRequired'));
    const res = await fetch(apiUrl(`/shipping/check-now/${transactionId}`), { method: 'POST', headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.shipmentCheckFailed'));
    return data;
  },

  // Betala fraktkostnaden för din sida av ett byte (om du valde frakt istället
  // för hämta) — se POST /checkout/swap-shipping-session. Returnerar en
  // Stripe-checkout-URL att navigera till, samma mönster som varukorgen.
  paySwapShipping: async (proposalId: string, quoteId?: string): Promise<{ sessionUrl: string }> => {
    const token = getAccessToken();
    if (!token) throw new Error(t('common.authRequired'));
    const res = await fetch(apiUrl('/checkout/swap-shipping-session'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ proposalId, quoteId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.paymentSessionFailed'));
    return data;
  },

  // ── FILE UPLOAD ───────────────────────────────────────────────────
  uploadFile: async (file: File): Promise<{ id: string; filename: string; url: string; type: string; originalName: string; size: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(apiUrl('/api/upload'), {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? t('api.uploadFailed'));
    return absolutizeUploadUrls(await res.json());
  },

  uploadFiles: async (files: File[]): Promise<{ files: Array<{ id: string; filename: string; url: string; type: string }>; count: number }> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const res = await fetch(apiUrl('/api/upload/multiple'), {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? t('api.uploadFailed'));
    return absolutizeUploadUrls(await res.json());
  },

  // ── BOKFÖRING I FORTNOX (admin) ──────────────────────────────────
  fortnox: {
    status: () => adminRest<FortnoxStatus>('GET', '/api/admin/fortnox/status'),
    connect: () => adminRest<{ url: string }>('POST', '/api/admin/fortnox/connect'),
    disconnect: () => adminRest<{ ok: true }>('POST', '/api/admin/fortnox/disconnect'),
    saveSettings: (voucherSeries: string, accounts: FortnoxAccountPlan, autoBookEnabled?: boolean) => adminRest<{ ok: true }>('PUT', '/api/admin/fortnox/settings', { voucherSeries, accounts, autoBookEnabled }),
    check: () => adminRest<{ problems: string[] }>('GET', '/api/admin/fortnox/check'),
    backfill: () => adminRest<{ created: number }>('POST', '/api/admin/fortnox/backfill'),
    preview: (from: string, to: string) => adminRest<FortnoxPreview>('GET', `/api/admin/fortnox/preview?from=${from}&to=${to}`),
    book: (from: string, to: string) => adminRest<{ vouchers: FortnoxVoucher[] }>('POST', '/api/admin/fortnox/book', { from, to }),
    retry: (id: string) => adminRest<{ voucher: FortnoxVoucher }>('POST', `/api/admin/fortnox/vouchers/${encodeURIComponent(id)}/retry`),
  },

  // ── BAKGRUNDSBORTTAGNING ──────────────────────────────────────────
  // Flagga + modellkonfiguration (styrs med env i API:t, se
  // images/backgroundRemovalConfig.ts). Utloggad eller fel = avstängd.
  /** Ändras vid in-/utloggning — för att cacha svar per användare (t.ex. getFeatures). */
  authKey: (): string => getAccessToken() ?? '',

  getFeatures: async (): Promise<{ backgroundRemoval: BgRemovalConfig }> => {
    try {
      const res = await fetch(apiUrl('/api/features'), { headers: authHeaders() });
      if (!res.ok) throw new Error(String(res.status));
      return await res.json();
    } catch {
      return { backgroundRemoval: DEFAULT_BG_REMOVAL_CONFIG };
    }
  },

  // Laddar upp en frilagd version av en redan uppladdad bild. Originalet rörs
  // inte; servern sparar den nya filen bredvid (…_cutout_<rand>.webp).
  uploadCutout: async (originalUrl: string, image: Blob): Promise<{ url: string; originalUrl: string }> => {
    const formData = new FormData();
    formData.append('originalUrl', originalUrl); // måste komma före filen
    formData.append('file', image, image.type === 'image/webp' ? 'cutout.webp' : 'cutout.jpg');
    const res = await fetch(apiUrl('/api/upload/cutout'), { method: 'POST', headers: authHeaders(), body: formData });
    if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? t('api.uploadFailed'));
    return absolutizeUploadUrls(await res.json());
  },

  // ── AI PRODUKTBILDER ────────────────────────────────────────────────
  generateProductImages: async (imageUrl: string, opts?: { name?: string; category?: string; modelPhotoUrl?: string }): Promise<{
    variants: { key: 'model'; label: string; url: string }[];
    errors?: string[];
  }> => {
    const token = getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    Object.assign(headers, authHeaders());
    const res = await fetch(apiUrl('/api/generate-product-images'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageUrl, name: opts?.name, category: opts?.category, modelPhotoUrl: opts?.modelPhotoUrl }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.aiImageFailed'));
    return absolutizeUploadUrls(data);
  },

  // ── TESTA PLAGG PÅ MIG ──────────────────────────────────────────────
  tryOnGarment: async (opts: {
    garmentImageUrl: string; modelPhotoUrl: string;
    garmentName?: string; garmentCategory?: string; garmentSize?: string;
    profileSize?: string; bustCm?: number; waistCm?: number; hipCm?: number;
  }): Promise<{ url: string }> => {
    const token = getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    Object.assign(headers, authHeaders());
    const res = await fetch(apiUrl('/api/try-on'), {
      method: 'POST',
      headers,
      body: JSON.stringify(opts),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.tryOnFailed'));
    return absolutizeUploadUrls(data);
  },

  // ── OUTFIT-FÖRSLAG (api/src/outfits) ──────────────────────────────────
  suggestOutfits: async (opts: { prompt?: string; imageUrl?: string; lang?: 'sv' | 'en' }): Promise<OutfitSuggestion> => {
    const res = await fetch(apiUrl('/api/outfits/suggest'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(opts),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('outfits.failed'));
    return absolutizeUploadUrls(data as OutfitSuggestion);
  },

  // Helkroppsbilden är privat: den hämtas med inloggning och visas som blob-URL.
  tryOnOutfit: async (suggestionId: string, outfitIndex: number, bodyPhotoUrl?: string): Promise<string> => {
    const res = await fetch(apiUrl(`/api/outfits/${encodeURIComponent(suggestionId)}/outfits/${outfitIndex}/try-on`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(bodyPhotoUrl ? { bodyPhotoUrl } : {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('outfits.tryOnFailed'));
    const img = await fetch(apiUrl(data.renderUrl), { headers: authHeaders() });
    if (!img.ok) throw new Error(t('outfits.tryOnFailed'));
    return URL.createObjectURL(await img.blob());
  },

  // ── Q-CHATTEN ────────────────────────────────────────────────────────
  qChat: async (messages: { role: 'user' | 'assistant'; content: string }[]): Promise<{
    reply: string;
    action?: 'start_drop' | 'vinted_import_ready' | 'outfits';
    outfits?: OutfitSuggestion;
    vintedUserId?: number;
    vintedLogin?: string;
    itemCount?: number;
  }> => {
    const token = getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    Object.assign(headers, authHeaders());
    const res = await fetch(apiUrl('/api/q-chat'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ messages, lang: getLang() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.qUnavailable'));
    return data;
  },

  startVintedImport: async (vintedUserId: number, itemCount: number): Promise<{ importId: string }> => {
    const token = getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    Object.assign(headers, authHeaders());
    const res = await fetch(apiUrl('/api/vinted-import/start'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ vintedUserId, itemCount }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.vintedImportFailed'));
    return data;
  },

  getVintedImportStatus: async (importId: string): Promise<{
    status: 'running' | 'done' | 'error'; done: number; total: number; capped: boolean; error?: string;
  }> => {
    const token = getAccessToken();
    const headers: Record<string, string> = {};
    Object.assign(headers, authHeaders());
    const res = await fetch(apiUrl(`/api/vinted-import/status/${importId}`), { headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.importStatusFailed'));
    return data;
  },

  analyzeGarment: async (imageUrl: string): Promise<{
    name: string; description: string; category: string; size: string; condition: number;
    salePrice: number; rentPrice3: number; rentPrice12: number; rentPrice24: number;
  }> => {
    const token = getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    Object.assign(headers, authHeaders());
    const res = await fetch(apiUrl('/api/q-analyze-garment'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageUrl }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error ?? t('api.analyzeFailed'));
    return data;
  },
};
